import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
  Avatar,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  List as ListIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

import productService from '../../services/productService';
import salesService from '../../services/salesService';
import { customerService } from '../../services/customerService';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import companySettingsService from '../../services/companySettingsService';
import printSettingsService from '../../services/printSettingsService';
import { shareBillToWhatsApp } from '../../utils/whatsappShareHelper';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import CustomerForm from './CustomerForm';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import HoldBillModal from './HoldBillModal'; // New import
import ViewHoldBillsModal from './ViewHoldBillsModal'; // New import
import { holdBillService } from '../../services/holdBillService'; // New import
import salesmanService from '../../services/salesmanService';
import loyaltySettingsService from '../../services/loyaltySettingsService';
import SplitBillDialog from './SplitBillDialog';
import { generateOfflineReceiptPdf } from '../../utils/offlineReceiptGenerator';
import { formatDate } from '../../utils/dateFormatter';
import { getDesktopOfflineCompanyId } from '../../utils/desktopOffline';

/** Avoid duplicate rows if SQLite ever contains the same id twice. */
function dedupeRowsById(rows) {
  const seen = new Set();
  return (rows || []).filter((r) => {
    if (r == null || r.id == null) return false;
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '15px',
  '&.focused': {
    backgroundColor: '#e3f2fd',
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: '1px solid #bdc3c7',
  backgroundColor: '#2c3e50',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '15px',
  textAlign: 'center',
}));

const FunctionKeyBar = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderTop: '2px solid #3498db',
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
}));

const FunctionKey = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  padding: '8px 12px',
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontSize: '13px',
  fontWeight: '600',
  textTransform: 'none',
  border: '1px solid #bdc3c7',
  '&:hover': {
    backgroundColor: '#bdc3c7',
  },
  '& .key': {
    fontWeight: 'bold',
    color: '#3498db',
    fontSize: '14px',
    marginRight: '4px',
  },
}));

const PAGE_KEY_POS_SALES_KEYBOARD = 'POS_SALES_KEYBOARD';

const PRINT_FORMATS = [
  { label: 'A4 Standard', value: 'A4' },
  { label: 'A4 Format 2 (Detailed GST)', value: 'A4_FORMAT_2' },
  { label: 'A4 Format 3 (Boxed Professional)', value: 'A4_FORMAT_3' },
  { label: 'A4 Format 4 (Legacy)', value: 'A4_FORMAT_4' },
  { label: 'A5 Format 1', value: 'A5_FORMAT_1' },
  { label: 'A5 Format 2 Double', value: 'A5_FORMAT_2_DOUBLE' },
  { label: 'A5 Format 3 Double GST', value: 'A5_FORMAT_2_DOUBLE_GST' },
  { label: 'A6 Format 1', value: 'A6_FORMAT_1' },
  { label: '3-inch Simple', value: '3_INCH_SIMPLE' },
  { label: '3-inch GST Details', value: '3_INCH_GST_DETAILS' },
  { label: '2-inch Standard', value: '2_INCH_SIMPLE' }
];

/** Backend may return LocalDate as ISO string or as [year, month, day] */
function parseExpiryTimestamp(expiryDate) {
  if (expiryDate == null) return null;
  if (Array.isArray(expiryDate) && expiryDate.length >= 3) {
    const y = Number(expiryDate[0]);
    const m = Number(expiryDate[1]);
    const d = Number(expiryDate[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    return new Date(y, m - 1, d).getTime();
  }
  if (typeof expiryDate === 'string') {
    const t = Date.parse(expiryDate);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Expiry date is inclusive through that calendar day; next day = expired */
function isBatchExpiredPos(expiryDate) {
  const t = parseExpiryTimestamp(expiryDate);
  if (t == null) return false;
  const exp = new Date(t);
  const today = new Date();
  const expDay = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate()).getTime();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return todayStart > expDay;
}

function formatExpiryForPos(expiryDate) {
  if (expiryDate == null) return '';
  if (Array.isArray(expiryDate) && expiryDate.length >= 3) {
    const y = Number(expiryDate[0]);
    const m = String(expiryDate[1]).padStart(2, '0');
    const d = String(expiryDate[2]).padStart(2, '0');
    return `${d}/${m}/${y}`;
  }
  // If it's YYYY-MM-DD string
  if (typeof expiryDate === 'string' && expiryDate.includes('-')) {
    const parts = expiryDate.split('-');
    if (parts.length === 3) {
      return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
  }
  return String(expiryDate);
}

/** Align with backend: INACTIVE batches must not appear in POS (defense in depth). */
function isBatchActiveForPos(b) {
  if (!b?.status) return true;
  return String(b.status).trim().toUpperCase() !== 'INACTIVE';
}

function sortBatchesByFefo(list) {
  return [...(list || [])].sort((a, b) => {
    const ta = parseExpiryTimestamp(a?.expiryDate);
    const tb = parseExpiryTimestamp(b?.expiryDate);
    if (ta == null && tb == null) return 0;
    if (ta == null) return 1;
    if (tb == null) return -1;
    return ta - tb;
  });
}

function getSoonestInStockBatch(batches) {
  const inStock = (batches || []).filter(
    (b) => Number(b?.quantity || 0) > 0 && !isBatchExpiredPos(b?.expiryDate)
  );
  const sorted = sortBatchesByFefo(inStock);
  return sorted[0] || null;
}

/** Round to 4 decimals to match backend DECIMAL(12,4) */
function roundPosQty(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000;
}

/** Numeric qty for math / API; empty draft in UI → 0 */
function lineItemQty(item) {
  const q = item?.quantity;
  if (q === '' || q === null || q === undefined) return 0;
  const n = Number(q);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * Batch/catalog sale rates are ex-GST. Non-GST bills store the same tax-inclusive unit price the customer pays
 * as on a normal GST line (so 350 @ 5% → 367.50).
 */
function exclusiveToStoredUnitPrice(nonGstMode, product, batchOrNull, exclusiveUnitPrice) {
  if (!nonGstMode) return Number(exclusiveUnitPrice) || 0;
  const rate = Number(batchOrNull?.tax?.rate ?? product?.tax?.rate ?? 0);
  if (!(rate > 0)) return Number(exclusiveUnitPrice) || 0;
  const ex = Number(exclusiveUnitPrice) || 0;
  return Math.round(ex * (1 + rate / 100) * 100) / 100;
}

function formatTotalQtyBadge(cart, allowDecimalQty) {
  const sum = cart.reduce((s, item) => (item.product ? s + lineItemQty(item) : s), 0);
  if (!Number.isFinite(sum)) return '0';
  if (!allowDecimalQty) return String(Math.floor(sum));
  return String(Number(roundPosQty(sum).toFixed(4)));
}

/** Quantity from API / hold bill — preserve decimals, default 1 */
function normalizeLoadedLineQuantity(q) {
  if (q == null) return 1;
  const n = Number(q);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return roundPosQty(n);
}

function lineUnitPriceFromItem(item) {
  const p = item?.price;
  if (p === '' || p === null || p === undefined) return 0;
  const n = Number(p);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Same base as handleOverallDiscountChange: used for overall % / amount discount math.
 * GST: subtotal + tax − line item discounts; Non-GST: subtotal after line discounts.
 */
function computeBaseForOverallDiscountFromCart(cart, nonGstMode) {
  const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
  if (nonGstMode) {
    return cart.reduce((sum, item) => {
      if (!item.product) return sum;
      return sum + lineUnitPriceFromItem(item) * lineItemQty(item) - (Number(item.discount) || 0);
    }, 0);
  }
  const subtotal = cart.reduce((sum, item) => {
    if (!item.product) return sum;
    return sum + lineUnitPriceFromItem(item) * lineItemQty(item);
  }, 0);
  const taxAmount = cart.reduce((sum, item) => {
    if (!item.product) return sum;
    const itemTotal = lineUnitPriceFromItem(item) * lineItemQty(item) - (Number(item.discount) || 0);
    const taxRate = (item.product?.tax?.rate || 0) / 100;
    return sum + itemTotal * taxRate;
  }, 0);
  return subtotal + taxAmount - itemDiscounts;
}

/** When loading a bill for edit, backend only stores discount rupees — derive % and use PERCENTAGE mode so line changes keep the same rate. */
function deriveOverallDiscountStateForLoadedOrder(orderDiscount, cartRows, nonGstFlag) {
  const od = Number(orderDiscount) || 0;
  if (od <= 0) {
    return { discountType: 'AMOUNT', overallDiscountPercentage: 0 };
  }
  const base = computeBaseForOverallDiscountFromCart(cartRows, nonGstFlag);
  if (!(base > 0)) {
    return { discountType: 'AMOUNT', overallDiscountPercentage: 0 };
  }
  const pct = (od / base) * 100;
  return {
    discountType: 'PERCENTAGE',
    overallDiscountPercentage: Math.round(pct * 100) / 100,
  };
}

/** Arrow keys: leave cell left when caret at start (or whole value selected). */
function posInputAllowsNavOutLeft(input) {
  if (!input || input.tagName !== 'INPUT' || typeof input.selectionStart !== 'number') return true;
  const s = input.selectionStart;
  const e = input.selectionEnd ?? s;
  const len = (input.value || '').length;
  if (len > 0 && s === 0 && e === len) return true;
  return s === 0 && e === 0;
}

/** Arrow keys: leave cell right when caret at end (or whole value selected). */
function posInputAllowsNavOutRight(input) {
  if (!input || input.tagName !== 'INPUT' || typeof input.selectionStart !== 'number') return true;
  const s = input.selectionStart;
  const e = input.selectionEnd ?? s;
  const len = (input.value || '').length;
  if (len > 0 && s === 0 && e === len) return true;
  return s === len && e === len;
}

/**
 * Vertical grid move: keep the same field (e.g. saleRate) when the next row has optional columns
 * like UOM — do not use numeric column index (that lands on the wrong field).
 */
function resolveVerticalNavTarget(fieldKey, sourceKeys, targetKeys) {
  if (!targetKeys.length) return fieldKey;
  if (targetKeys.includes(fieldKey)) return fieldKey;
  const idx = sourceKeys.indexOf(fieldKey);
  if (idx < 0) return targetKeys[0];
  for (let i = idx - 1; i >= 0; i--) {
    const k = sourceKeys[i];
    if (targetKeys.includes(k)) return k;
  }
  for (let i = idx + 1; i < sourceKeys.length; i++) {
    const k = sourceKeys[i];
    if (targetKeys.includes(k)) return k;
  }
  return targetKeys[Math.min(idx, targetKeys.length - 1)];
}

/** Customer dropdown row tooltip: hover works, and keyboard-highlighted row stays open (MUI Tooltip is hover-only unless controlled). */
function PosCustomerOptionTooltip({ keyboardHighlighted, title, children }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  useEffect(() => {
    if (!keyboardHighlighted) setHoverOpen(false);
  }, [keyboardHighlighted]);
  const open = Boolean(keyboardHighlighted) || hoverOpen;
  return (
    <Tooltip
      title={title}
      placement="right"
      enterDelay={350}
      arrow
      open={open}
      onOpen={() => setHoverOpen(true)}
      onClose={() => setHoverOpen(false)}
    >
      {children}
    </Tooltip>
  );
}

const POSKeyboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Refs for input cells
  const itemCodeRefs = useRef([]);
  const qtyRefs = useRef([]);
  const priceRefs = useRef([]);
  const inclusivePriceRefs = useRef([]);
  const discountPctRefs = useRef([]);
  const discountRefs = useRef([]);
  const uomRefs = useRef([]);
  const deleteNoRef = useRef(null);
  const deleteYesRef = useRef(null);
  const handleTabOrderEnterRef = useRef(null);
  const customerInputRef = useRef(null);
  /** When UOM menu closes via Escape/backdrop, do not auto-advance tab order (Select onClose still runs for item commit). */
  const uomSkipTabAdvanceRef = useRef(false);

  // Page field visibility and tab order (POS Sales Keyboard)
  const [pageFieldConfig, setPageFieldConfig] = useState({
    mrp: true,
    gst: true,
    uom: true,
    discount: true,
    showItemHistory: true,
    showSaleRate: true,
    showSaleRateInclusive: false,
    showPurchaseRate: false,
    autoApplyPreviousPrice: false,
    allowNegativeStock: false,
    allowDecimalQty: false
  });
  const [tabOrderConfig, setTabOrderConfig] = useState({});
  const [openUomRowIndex, setOpenUomRowIndex] = useState(null);
  const [roundOffEnabled, setRoundOffEnabled] = useState(false);
  const [roundOffMode, setRoundOffMode] = useState('NEAREST'); // DOWN | UP | NEAREST
  const [initialFocus, setInitialFocus] = useState('GRID'); // CUSTOMER | BARCODE | GRID
  const initialFocusAppliedRef = useRef(false);

  const applyConfiguredInitialFocus = useCallback((opts = {}) => {
    const { force = false } = opts;
    const mode = (initialFocus || 'GRID').toUpperCase();

    // If user is actively typing in an input, avoid stealing focus unless forced.
    if (!force) {
      const ae = document.activeElement;
      const isAlreadyInInput =
        ae &&
        (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.getAttribute?.('contenteditable') === 'true');
      if (isAlreadyInInput) return;
    }

    if (mode === 'CUSTOMER') {
      customerInputRef.current?.focus?.();
      customerInputRef.current?.select?.();
      return;
    }

    // BARCODE and GRID both focus the first Item Code cell (scan/typing area)
    if (itemCodeRefs.current[0]) {
      itemCodeRefs.current[0].focus();
      itemCodeRefs.current[0].select?.();
    }
  }, [initialFocus]);

  // State
  const [cart, setCart] = useState([
    { id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null, productBatchId: null, batchNumber: null, expiryDate: null, mfd: null, selectedBatch: null },
  ]);
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  /** Customer row highlighted in the Autocomplete list (arrow keys / mouse) — show address without widening options */
  const [customerListHighlight, setCustomerListHighlight] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [splitBillDialogOpen, setSplitBillDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  const [overallDiscountPercentage, setOverallDiscountPercentage] = useState(0);
  const [discountType, setDiscountType] = useState('AMOUNT');
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const [productBatchesByProduct, setProductBatchesByProduct] = useState({});
  const autocompleteRef = useRef(null);
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);
  const [enableBatchWise, setEnableBatchWise] = useState(true);
  const [directPrint, setDirectPrint] = useState(false);
  const [previewPrint, setPreviewPrint] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [previewPrintDialogOpen, setPreviewPrintDialogOpen] = useState(false);
  const [printChoiceDialogOpen, setPrintChoiceDialogOpen] = useState(false);
  const [lastCreatedOrder, setLastCreatedOrder] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [units, setUnits] = useState([]);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);

  // Salesman (optional - for attributing bill to a salesman; no login)
  const [salesmen, setSalesmen] = useState([]);
  const [selectedSalesmanId, setSelectedSalesmanId] = useState('');

  // Loyalty: settings (point value), redeemed points on this bill
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [redeemedPoints, setRedeemedPoints] = useState(0);

  // Non-GST mode: Sale Rate shows tax-included rate, GST column shows 0, total same (Ctrl+Q to toggle)
  const [nonGstMode, setNonGstMode] = useState(false);
  /** Next invoice number preview from API (same sequence as backend will assign on save) */
  const [nextOrderNumber, setNextOrderNumber] = useState('');

  // Quotation / edit mode state
  const [isQuotationMode, setIsQuotationMode] = useState(false);
  const [quotationId, setQuotationId] = useState(null); // Store quotation ID when loading from View Orders
  const [editOrderId, setEditOrderId] = useState(null); // Store existing order ID when editing a bill
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  /** Earlier-expiry batch reminder when cashier picks a later-expiring batch */
  const [batchEarlierExpiryDialog, setBatchEarlierExpiryDialog] = useState(null);
  const [purchasePriceDialogOpen, setPurchasePriceDialogOpen] = useState(false);
  const [purchasePriceDialogData, setPurchasePriceDialogData] = useState(null); // { productName, purchasePrice }
  const [holdBillModalOpen, setHoldBillModalOpen] = useState(false);
  const [viewHoldBillsModalOpen, setViewHoldBillsModalOpen] = useState(false);
  const [user, setUser] = useState(getCurrentUser());
  const [recentOrders, setRecentOrders] = useState([]);
  const [productHistory, setProductHistory] = useState([]);
  const [productHistoryLoading, setProductHistoryLoading] = useState(false);
  const [productHistoryError, setProductHistoryError] = useState(null);
  const [productHistoryTab, setProductHistoryTab] = useState('recentBills'); // 'recentBills' | 'productHistory'
  const [salesHistoryDialogOpen, setSalesHistoryDialogOpen] = useState(false);
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(false);
  const [salesHistorySearch, setSalesHistorySearch] = useState('');
  const [salesHistoryPage, setSalesHistoryPage] = useState(0);
  const [salesHistoryRowsPerPage, setSalesHistoryRowsPerPage] = useState(10);
  const [showPrintSchemeSelection, setShowPrintSchemeSelection] = useState(false);
  const [printSchemeDialogOpen, setPrintSchemeDialogOpen] = useState(false);
  const [selectedPrintScheme, setSelectedPrintScheme] = useState('A4');

  useEffect(() => {
    setSalesHistoryPage(0);
  }, [salesHistorySearch]);

  // Price mode: one rate type for the whole transaction (retailer or wholesale)
  const [priceMode, setPriceMode] = useState('retailer'); // 'retailer' | 'wholesale'

  // Clock state for real-time display in top bar
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load page field visibility and tab order config
  // Note: getPageFieldConfig returns the fieldConfig object (e.g. { mrp, gst, uom, discount, focusOrder })
  useEffect(() => {
    let cancelled = false;
    companySettingsService
      .getPageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD)
      .then((fieldConfig) => {
        if (!cancelled && fieldConfig && typeof fieldConfig === 'object') {
          setPageFieldConfig((prev) => ({
            mrp: fieldConfig.mrp !== false,
            gst: fieldConfig.gst !== false,
            uom: fieldConfig.uom !== false,
            discount: fieldConfig.discount !== false,
            showItemHistory: fieldConfig.showItemHistory !== false,
            showSaleRate: fieldConfig.showSaleRate !== false,
            showSaleRateInclusive: Boolean(fieldConfig.showSaleRateInclusive),
            showPurchaseRate: fieldConfig.showPurchaseRate === true,
            autoApplyPreviousPrice: Boolean(fieldConfig.autoApplyPreviousPrice),
            allowNegativeStock: Boolean(fieldConfig.allowNegativeStock),
            allowDecimalQty: Boolean(fieldConfig.allowDecimalQty)
          }));
          const focusOrder = fieldConfig.focusOrder;
          setTabOrderConfig(focusOrder && typeof focusOrder === 'object' ? focusOrder : {});
          setRoundOffEnabled(Boolean(fieldConfig.roundOffEnabled));
          setRoundOffMode(fieldConfig.roundOffMode || 'NEAREST');
          setInitialFocus(fieldConfig.initialFocus || 'GRID');
        }
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  // Load company-level batch/expiry feature toggle
  useEffect(() => {
    let cancelled = false;
    companySettingsService
      .getSettings()
      .then((data) => {
        if (cancelled) return;
        setEnableBatchWise(Boolean(data?.enableBatchWise ?? true));
      })
      .catch(() => {
        if (!cancelled) setEnableBatchWise(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When batch-wise feature is disabled, normalize cart items so backend receives no batch ids.
  useEffect(() => {
    if (enableBatchWise) return;
    setCart((prev) =>
      (Array.isArray(prev) ? prev : []).map((ci) =>
        ci.product
          ? {
            ...ci,
            productBatchId: null,
            batchNumber: null,
            expiryDate: null,
            mfd: null,
            selectedBatch: null
          }
          : ci
      )
    );
  }, [enableBatchWise]);

  // Listen for company information updates
  useEffect(() => {
    const handleCompanyUpdate = () => {
      setUser(getCurrentUser());
    };

    window.addEventListener('companyInfoUpdated', handleCompanyUpdate);
    window.addEventListener('storage', handleCompanyUpdate);

    return () => {
      window.removeEventListener('companyInfoUpdated', handleCompanyUpdate);
      window.removeEventListener('storage', handleCompanyUpdate);
    };
  }, []);

  // When price mode (or Non-GST) changes, refresh line unit prices from catalog/batch — Non-GST lines stay tax-inclusive.
  useEffect(() => {
    setCart((prev) =>
      prev.map((item) => {
        if (!item.product) return item;
        if (item.selectedBatch?.sellingPrice != null && item.selectedBatch.sellingPrice !== '') {
          return {
            ...item,
            price: exclusiveToStoredUnitPrice(
              nonGstMode,
              item.product,
              item.selectedBatch,
              Number(item.selectedBatch.sellingPrice)
            ),
          };
        }
        const availableUnits = getAvailableUnits(item.product);
        const selectedUnitData = item.selectedUnit
          ? availableUnits.find((u) => u.id === item.selectedUnit?.id)
          : availableUnits[0];
        const catalogExclusive = selectedUnitData ? selectedUnitData.price : getSaleRateForMode(item.product);
        return {
          ...item,
          price: exclusiveToStoredUnitPrice(nonGstMode, item.product, null, catalogExclusive),
        };
      })
    );
  }, [priceMode]); // eslint-disable-line react-hooks/exhaustive-deps -- retail/wholesale switch only; nonGstMode is read when this runs

  const fetchSalesHistory = useCallback(async () => {
    setSalesHistoryLoading(true);
    try {
      const isWalkIn = customer?.name?.toLowerCase().includes('walk-in');
      let data;

      if (customer?.id && !isWalkIn) {
        // Fetch specific customer history
        const response = await salesService.getSalesOrdersByCustomer(customer.id);
        data = Array.isArray(response) ? response : (response.content || []);
      } else {
        // Fetch overall history
        const response = await salesService.getAllSalesOrders({
          page: 0,
          size: 50,
          sort: 'createdAt',
          direction: 'DESC',
          excludeStatus: 'RETURNED'
        });
        data = response.content || [];
      }
      const filteredHistory = (data || []).filter((order) => {
        const status = String(order?.status || '').trim().toUpperCase();
        return status !== 'CANCELLED' && status !== 'RETURNED';
      });
      setSalesHistory(filteredHistory);
    } catch (error) {
      console.error('Error fetching sales history:', error);
      enqueueSnackbar('Failed to fetch sales history', { variant: 'error' });
    } finally {
      setSalesHistoryLoading(false);
    }
  }, [enqueueSnackbar, customer]);

  useEffect(() => {
    if (salesHistoryDialogOpen) {
      fetchSalesHistory();
    }
  }, [salesHistoryDialogOpen, fetchSalesHistory]);

  const handleReprintBill = async (orderId) => {
    try {
      enqueueSnackbar('Generating receipt...', { variant: 'info' });
      const receiptBlob = await salesService.generateReceipt(orderId, 'CUSTOMER', null);
      const url = window.URL.createObjectURL(receiptBlob);
      window.open(url, '_blank');

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
    } catch (error) {
      console.error('Error reprinting bill:', error);
      enqueueSnackbar('Failed to reprint bill', { variant: 'error' });
    }
  };

  const handleEditBillFromHistory = async (order) => {
    try {
      setSalesHistoryDialogOpen(false);
      enqueueSnackbar(`Loading bill #${order.orderNumber || order.id}...`, { variant: 'info' });

      // Fetch full order data if items are missing
      let fullOrder = order;
      if (!order.items || order.items.length === 0) {
        fullOrder = await salesService.getSalesOrderById(order.id);
      }

      // Set customer
      if (fullOrder.customer) {
        setCustomer(fullOrder.customer);
      }

      // Load items into cart
      if (fullOrder.items && fullOrder.items.length > 0) {
        const loadedDisc = Number(fullOrder.discount) || 0;
        const cartItems = fullOrder.items.map((item, index) => {
          const product = {
            ...(item.product || {}),
            id: item.productId || item.product?.id,
            name: item.productName || item.product?.name || 'Unknown Product',
            salePrice: item.unitPrice,
            sellingPrice: item.unitPrice,
            tax: item.tax || item.product?.tax || null
          };

          return {
            id: index + 1,
            itemCode: product.sku || product.barcode || '',
            productName: product.name,
            quantity: normalizeLoadedLineQuantity(item.quantity),
            price: item.unitPrice,
            discount: item.discount || 0,
            discountPercentage: item.discountPercentage || 0,
            discountAmount: item.discountAmount || item.discount || 0,
            product: product,
            selectedUnit: item.unit || null,
            productBatchId: enableBatchWise ? (item.productBatchId || null) : null,
            batchNumber: enableBatchWise ? (item.batchNumber || null) : null,
            expiryDate: enableBatchWise ? (item.expiryDate || null) : null,
            mfd: enableBatchWise ? (item.mfd || null) : null
          };
        });

        // Add one empty row at the end
        cartItems.push({
          id: cartItems.length + 1,
          itemCode: '', productName: '', quantity: 1, price: 0,
          discount: 0, discountPercentage: 0, discountAmount: 0,
          product: null, selectedUnit: null, productBatchId: null,
          batchNumber: null, expiryDate: null, mfd: null, selectedBatch: null
        });

        const derived = deriveOverallDiscountStateForLoadedOrder(loadedDisc, cartItems, !!fullOrder.nonGst);
        setDiscountType(derived.discountType);
        setOverallDiscountPercentage(derived.overallDiscountPercentage);
        setDiscount(loadedDisc);
        setCart(cartItems);
      } else if (fullOrder.discount) {
        setDiscount(Number(fullOrder.discount));
      }

      setEditOrderId(fullOrder.id);
      setIsQuotationMode(false);
      setNonGstMode(!!fullOrder.nonGst);

      enqueueSnackbar('Bill loaded for editing.', { variant: 'success' });
    } catch (error) {
      console.error('Error loading bill for edit:', error);
      enqueueSnackbar('Failed to load bill for editing', { variant: 'error' });
    }
  };

  const filteredSalesHistory = useMemo(() => {
    if (!salesHistorySearch.trim()) return salesHistory;
    const lowerQuery = salesHistorySearch.toLowerCase();
    return salesHistory.filter(o =>
      (o.orderNumber || '').toLowerCase().includes(lowerQuery) ||
      (o.id || '').toString().includes(lowerQuery) ||
      (o.customerName || o.customer?.name || '').toLowerCase().includes(lowerQuery) ||
      (o.total || '').toString().includes(lowerQuery)
    );
  }, [salesHistory, salesHistorySearch]);

  // Derived visibility flags for data table columns only. Item Code & Product Name are fixed.
  const showMrp = pageFieldConfig.mrp !== false;
  const showGst = pageFieldConfig.gst !== false;
  const showUom = pageFieldConfig.uom !== false;
  const showDiscount = pageFieldConfig.discount !== false;
  const showPurchaseRate = pageFieldConfig.showPurchaseRate === true;

  // Tab order follows POS Settings. UOM can be included and is applied only for rows with multiple UOM choices.
  const orderedFieldKeys = React.useMemo(() => {
    const config = tabOrderConfig || {};
    const keys = ['itemCode', 'qty', 'uom', 'saleRate', 'saleRateInclusive', 'discount'];
    const withOrder = keys
      .filter((k) => (k !== 'saleRate' || pageFieldConfig.showSaleRate !== false))
      .filter((k) => (k !== 'saleRateInclusive' || pageFieldConfig.showSaleRateInclusive))
      .filter((k) => (k !== 'uom' || pageFieldConfig.uom))
      .filter((k) => (k !== 'discount' || pageFieldConfig.discount))
      .map((k) => ({ key: k, order: config[k] != null && config[k] !== '' ? Number(config[k]) : null }))
      .filter((x) => x.order != null && x.order > 0);

    if (withOrder.length === 0) {
      return [
        'itemCode',
        'qty',
        ...(pageFieldConfig.uom !== false ? ['uom'] : []),
        ...(pageFieldConfig.showSaleRate !== false ? ['saleRate'] : []),
        ...(pageFieldConfig.showSaleRateInclusive ? ['saleRateInclusive'] : []),
        ...(pageFieldConfig.discount ? ['discount'] : []),
      ];
    }
    withOrder.sort((a, b) => a.order - b.order);
    return withOrder.map((x) => x.key);
  }, [
    tabOrderConfig,
    pageFieldConfig.uom,
    pageFieldConfig.showSaleRate,
    pageFieldConfig.showSaleRateInclusive,
    pageFieldConfig.discount
  ]);

  const focusRef = useCallback((key, rowIndex) => {
    const r = key === 'itemCode' ? itemCodeRefs.current[rowIndex]
      : key === 'qty' ? qtyRefs.current[rowIndex]
        : key === 'uom' ? uomRefs.current[rowIndex]
          : key === 'saleRate' ? priceRefs.current[rowIndex]
            : key === 'saleRateInclusive' ? inclusivePriceRefs.current[rowIndex]
              : key === 'discountPct' ? discountPctRefs.current[rowIndex]
                : key === 'discount' ? discountRefs.current[rowIndex]
                  : null;
    if (r) {
      r.focus?.();
      if (r.select) r.select();
      if (key === 'uom') {
        setOpenUomRowIndex(rowIndex);
      }
    }
  }, []);

  // On first load, keep focus on customer selector (not item code)
  useEffect(() => {
    // Do nothing here so that MUI's Autocomplete for customer keeps the initial focus.
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const getCustomerAddressDisplay = (c) => {
    if (!c || c.id === 'ADD_NEW') return '';
    const t = c.address != null ? String(c.address).trim() : '';
    return t;
  };

  // Load recent orders
  const loadRecentOrders = useCallback(async () => {
    try {
      const response = await salesService.getAllSalesOrders({ page: 0, size: 3, sort: 'createdAt', direction: 'DESC' });
      const orders = response.content || [];
      setRecentOrders(orders);
    } catch (error) {
      console.error('Failed to load recent orders', error);
    }
  }, []);

  const refreshNextOrderNumber = useCallback(async () => {
    try {
      const n = await salesService.getNextOrderNumber(nonGstMode);
      const s = n != null ? String(n).trim() : '';
      setNextOrderNumber(s);
    } catch (error) {
      console.error('Failed to load next order number', error);
      setNextOrderNumber('');
    }
  }, [nonGstMode]);

  useEffect(() => {
    refreshNextOrderNumber();
  }, [refreshNextOrderNumber]);

  const formatHistoryOrderDate = (orderDate) => {
    if (!orderDate) return '';

    // Handle yyyyddd or yyyydd formats (year + day-of-year) like 2026317
    if (/^\d{7}$/.test(orderDate)) {
      const year = Number(orderDate.substring(0, 4));
      const dayOfYear = Number(orderDate.substring(4));
      const date = new Date(year, 0);
      date.setDate(dayOfYear);
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      return `${d}/${m}/${year}`;
    }
    if (/^\d{6}$/.test(orderDate)) {
      const year = Number(orderDate.substring(0, 4));
      const dayOfYear = Number(orderDate.substring(4));
      const date = new Date(year, 0);
      date.setDate(dayOfYear);
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      return `${d}/${m}/${year}`;
    }

    // Fallback: try to parse as ISO/string date
    const parsed = new Date(orderDate);
    if (!isNaN(parsed.getTime())) {
      const d = String(parsed.getDate()).padStart(2, '0');
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const y = parsed.getFullYear();
      return `${d}/${m}/${y}`;
    }

    return orderDate;
  };

  const loadProductHistory = useCallback(
    async (product, currentCustomer) => {
      if (!product || !product.id || !currentCustomer || !currentCustomer.id) {
        setProductHistory([]);
        return;
      }

      try {
        setProductHistoryLoading(true);
        setProductHistoryError(null);

        // Fetch all past sales for this customer
        const orders = await salesService.getSalesOrdersByCustomer(currentCustomer.id);

        // Normalize to newest-first so latest sold price stays on top.
        const sortedOrders = [...(orders || [])].sort((a, b) => {
          const toEpoch = (v) => {
            if (!v) return 0;
            if (Array.isArray(v) && v.length >= 3) {
              const [y, m, d] = v;
              return new Date(Number(y), Number(m) - 1, Number(d)).getTime() || 0;
            }
            const t = new Date(v).getTime();
            return Number.isFinite(t) ? t : 0;
          };
          return toEpoch(b?.orderDate) - toEpoch(a?.orderDate);
        });

        // Pick matching lines from newest to oldest
        const history = [];
        for (let i = 0; i < sortedOrders.length && history.length < 5; i += 1) {
          const order = sortedOrders[i];
          const items = order.items || [];

          items.forEach((it) => {
            if (history.length >= 5) return;
            if (it.productId === product.id || it.product?.id === product.id) {
              const baseUnitPrice = Number(it.unitPrice || 0);
              const taxRate = Number(it.tax?.rate || it.product?.tax?.rate || 0);
              const isNonGstBill = order?.nonGst === true;
              const soldPriceWithTax = isNonGstBill
                ? baseUnitPrice
                : (baseUnitPrice * (1 + (taxRate / 100)));
              history.push({
                invoiceNo: order.orderNumber,
                productName: it.productName || it.product?.name || product.name,
                // Show effective sold unit price (inclusive of GST where applicable)
                price: Number.isFinite(soldPriceWithTax) ? Number(soldPriceWithTax.toFixed(2)) : baseUnitPrice,
                orderDate: order.orderDate,
              });
            }
          });
        }

        setProductHistory(history);
      } catch (error) {
        console.error('Failed to load product history', error);
        setProductHistoryError('Could not load product history');
        setProductHistory([]);
      } finally {
        setProductHistoryLoading(false);
      }
    },
    []
  );

  // Helper to get customer's due/credit balance (consistent with POS screen)
  const getCustomerCreditBalance = (cust) => {
    if (!cust) return 0;
    const creditValue = cust.creditBalance || cust.credit_balance || cust.credit || 0;
    return Number(creditValue) || 0;
  };

  const lineUnitPrice = useCallback((item) => {
    const p = item?.price;
    if (p === '' || p === null || p === undefined) return 0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  }, []);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);

    if (nonGstMode) {
      // Non-GST: cart prices are tax-inclusive; display tax as 0
      const subtotal = cart.reduce((sum, item) => {
        if (!item.product) return sum;
        return sum + (lineUnitPrice(item) * lineItemQty(item)) - (Number(item.discount) || 0);
      }, 0);
      const taxAmount = 0;
      const rawTotal = subtotal - discount;

      let finalTotal = rawTotal;
      let roundOff = 0;
      if (roundOffEnabled) {
        const rupees = Math.floor(rawTotal);
        const paise = rawTotal - rupees;
        if (roundOffMode === 'DOWN') {
          finalTotal = Math.floor(rawTotal);
        } else if (roundOffMode === 'UP') {
          finalTotal = Math.ceil(rawTotal);
        } else {
          // NEAREST: below .50 -> down, .50 and above -> up
          finalTotal = paise >= 0.5 ? Math.ceil(rawTotal) : Math.floor(rawTotal);
        }
        roundOff = finalTotal - rawTotal;
      }

      return { subtotal, taxAmount, total: finalTotal, discount: discount + itemDiscounts, roundOff };
    }

    const subtotal = cart.reduce((sum, item) => {
      if (!item.product) return sum;
      return sum + (lineUnitPrice(item) * lineItemQty(item));
    }, 0);

    const taxAmount = cart.reduce((sum, item) => {
      if (!item.product) return sum;
      const itemTotal = (lineUnitPrice(item) * lineItemQty(item)) - (item.discount || 0);
      const taxRate = (item.product?.tax?.rate || 0) / 100;
      return sum + (itemTotal * taxRate);
    }, 0);

    const rawTotal = subtotal + taxAmount - itemDiscounts - discount;

    let finalTotal = rawTotal;
    let roundOff = 0;
    if (roundOffEnabled) {
      const rupees = Math.floor(rawTotal);
      const paise = rawTotal - rupees;
      if (roundOffMode === 'DOWN') {
        finalTotal = Math.floor(rawTotal);
      } else if (roundOffMode === 'UP') {
        finalTotal = Math.ceil(rawTotal);
      } else {
        // NEAREST: below .50 -> down, .50 and above -> up
        finalTotal = paise >= 0.5 ? Math.ceil(rawTotal) : Math.floor(rawTotal);
      }
      roundOff = finalTotal - rawTotal;
    }

    return { subtotal, taxAmount, total: finalTotal, discount: discount + itemDiscounts, roundOff };
  }, [cart, discount, nonGstMode, roundOffEnabled, roundOffMode, lineUnitPrice]);

  // Focus Delete Yes button when dialog opens
  useEffect(() => {
    if (deleteConfirmOpen) {
      setTimeout(() => {
        deleteYesRef.current?.focus();
      }, 100);
    }
  }, [deleteConfirmOpen]);

  // Sync overall discount amount when cart or percentage changes
  // Sync overall discount amount when cart or percentage changes
  useEffect(() => {
    const baseForDiscount = computeBaseForOverallDiscountFromCart(cart, nonGstMode);

    if (discountType === 'PERCENTAGE' && overallDiscountPercentage > 0) {
      const newDiscountAmt = baseForDiscount * (overallDiscountPercentage / 100);
      const roundedDiscount = Math.round(newDiscountAmt * 100) / 100;
      if (Math.abs(roundedDiscount - discount) > 0.001) {
        setDiscount(roundedDiscount);
      }
    } else if (discountType === 'AMOUNT') {
      const recalcPercent = baseForDiscount > 0 ? (discount / baseForDiscount) * 100 : 0;
      const roundedPercent = Math.round(recalcPercent * 100) / 100;
      if (Math.abs(roundedPercent - overallDiscountPercentage) > 0.001) {
        setOverallDiscountPercentage(roundedPercent);
      }
    }
  }, [cart, nonGstMode, overallDiscountPercentage, discountType, discount]);

  // Load all products initially
  const loadAllProducts = useCallback(async () => {
    try {
      // Check if user has a location assigned
      let user = JSON.parse(localStorage.getItem('user')) || {};
      let userLocationData = user?.location || null;
      let hasLocation = userLocationData && userLocationData.id;

      // If location is missing, try to fetch current user data from backend
      if (!hasLocation) {
        console.log('[POSKeyboard] Location not found in localStorage, fetching current user from backend...');
        try {
          const axios = (await import('../../utils/axios')).default;
          const response = await axios.get('/auth/me');
          if (response.data && response.data.location) {
            user = response.data;
            userLocationData = response.data.location;
            hasLocation = userLocationData && userLocationData.id;
            // Update localStorage with full user object
            localStorage.setItem('user', JSON.stringify(user));
            console.log('[POSKeyboard] ✅ Fetched user location from backend:', userLocationData);
          }
        } catch (fetchError) {
          console.warn('[POSKeyboard] Could not fetch user location from backend:', fetchError);
        }
      }

      if (hasLocation) {
        console.log('[POSKeyboard] ✅ Location user detected:', userLocationData.name, '(ID:', userLocationData.id + ')');
      } else {
        console.log('[POSKeyboard] ℹ️ Warehouse/Admin user - no location assigned');
      }

      let activeProducts = [];

      if (hasLocation) {
        // Location user: Fetch products from location_products table
        console.log('[POSKeyboard] Location user detected - fetching location products for location:', userLocationData.name);
        const locationProducts = await productService.getLocationProducts();

        console.log('[POSKeyboard] Total location products received:', (locationProducts || []).length);

        // Convert location products to product format and filter by active status
        activeProducts = (locationProducts || [])
          .map(lp => ({
            id: lp.productId,
            name: lp.productName,
            sku: lp.sku || lp.productCode, // Use actual SKU if available, fallback to productCode
            barcode: lp.barcode || lp.productBarcode || lp.productCode,
            code: lp.productCode,
            quantity: lp.quantity,
            sellingPrice: lp.sellingPrice || lp.purchasePrice || 0,
            salePrice: lp.sellingPrice || lp.purchasePrice || 0,
            unitPrice: lp.sellingPrice || lp.purchasePrice || 0,
            wholesalePrice: lp.wholesalePrice != null ? lp.wholesalePrice : (lp.wholesale_price != null ? lp.wholesale_price : null),
            purchasePrice: lp.purchasePrice || 0,
            minSalePrice: lp.minSalePrice || lp.sellingPrice || 0,
            imageUrl: lp.imageUrl,
            status: lp.status || 'ACTIVE',
            locationProductId: lp.id,
            // Repacked products now own their stock; availableForSale is null from backend → use lp.quantity directly.
            locationQuantity: lp.availableForSale != null ? Number(lp.availableForSale) : (lp.quantity != null ? Number(lp.quantity) : 0),
            locationId: lp.locationId,
            locationName: lp.locationName,
            unit: lp.unit || lp.productUnit || null,
            parentProductId: lp.parentProductId ?? null
          }))
          .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');

        console.log('[POSKeyboard] Loaded', activeProducts.length, 'active location products');
      } else {
        // Warehouse/Admin user: Fetch from products table
        console.log('[POSKeyboard] Warehouse/Admin user - fetching all products');
        const res = await productService.getAllProducts(0, 1000, 'name', 'asc');
        const list = Array.isArray(res) ? res : (res?.content || []);
        activeProducts = list.filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
        console.log('[POSKeyboard] Loaded', activeProducts.length, 'warehouse products');
      }

      setAllProducts(activeProducts);
      console.log('Loaded products for autocomplete:', activeProducts.length);
      console.log('Sample products:', activeProducts.slice(0, 3).map(p => ({ name: p.name, sku: p.sku, status: p.status })));
    } catch (e) {
      console.error('Failed to load products', e);
      // Desktop offline: try local SQLite first (faster when backend is down)
      if (window.electronAPI?.getOfflineProducts) {
        try {
          const offlineList = await window.electronAPI.getOfflineProducts(getDesktopOfflineCompanyId());
          const mapped = dedupeRowsById(offlineList).map(p => ({
            id: p.id,
            name: p.name,
            sku: p.sku,
            barcode: p.barcode,
            sellingPrice: p.selling_price ?? 0,
            salePrice: p.selling_price ?? 0,
            unitPrice: p.selling_price ?? 0,
            quantity: p.quantity ?? 0,
            unit: p.unit_name ? { id: p.unit_id, name: p.unit_name } : null,
            status: p.status || 'ACTIVE',
            parentProductId: p.parent_product_id ?? p.parentProductId ?? null
          }));
          setAllProducts(mapped);
          console.log('[POSKeyboard] Loaded products from offline DB:', mapped.length);
          return;
        } catch (e3) {
          console.error('Offline products fallback failed', e3);
        }
      }
      // Fallback: try search (when online but main endpoint failed)
      try {
        const fallbackRes = await productService.searchProducts('a');
        const fallbackList = Array.isArray(fallbackRes) ? fallbackRes : (fallbackRes?.content || []);
        const fallbackActive = fallbackList.filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
        setAllProducts(fallbackActive);
        console.log('Loaded products (fallback):', fallbackActive.length);
      } catch (e2) {
        console.error('Fallback also failed', e2);
      }
    }
  }, []);

  // Load units
  const loadUnits = useCallback(async () => {
    try {
      const user = getCurrentUser();
      const companyId = user?.companyId || 1;
      const response = await unitService.getUnitsByCompany(companyId);
      const unitsList = Array.isArray(response) ? response : (response?.content || []);
      setUnits(unitsList);
      console.log('Loaded units:', unitsList.length);
    } catch (error) {
      console.error('Failed to load units:', error);
      // Set default units if API fails
      setUnits([
        { id: 1, name: 'Piece', abbreviation: 'PCS' },
        { id: 2, name: 'Box', abbreviation: 'BOX' },
        { id: 3, name: 'Kilogram', abbreviation: 'KG' },
        { id: 4, name: 'Gram', abbreviation: 'GM' },
        { id: 5, name: 'Liter', abbreviation: 'L' },
        { id: 6, name: 'Meter', abbreviation: 'M' }
      ]);
    }
  }, []);

  // Load customers
  const loadCustomers = useCallback(async () => {
    try {
      const res = await customerService.getAllCustomers({ page: 0, size: 1000, sort: 'name', direction: 'ASC' });
      const list = Array.isArray(res) ? res : (res?.content || []);
      setCustomers(list);

      // Always set "Walk-in Customer" as default if not already set
      // This ensures it's set on initial load and after refresh
      if (list.length > 0) {
        // Find Walk-in Customer by email, name, or customer ID
        const walkInCustomer = list.find(c =>
          (c.email && c.email.toLowerCase().includes('walkin')) ||
          (c.name && c.name.toLowerCase().includes('walk-in customer')) ||
          (c.customerId === '0000000000' || c.customerId === 0)
        );

        if (walkInCustomer) {
          // Only set if customer is not already set or if it's null
          setCustomer(prevCustomer => {
            if (!prevCustomer) {
              console.log('Default customer set to:', walkInCustomer.name);
              return walkInCustomer;
            }
            return prevCustomer;
          });
        }
      }
    } catch (e) {
      console.error('Failed to load customers', e);
      // Desktop offline: load from local SQLite
      if (window.electronAPI?.getOfflineCustomers) {
        try {
          const offlineList = await window.electronAPI.getOfflineCustomers(getDesktopOfflineCompanyId());
          const mapped = dedupeRowsById(offlineList).map(c => ({
            id: c.id,
            name: c.name,
            phone: c.phone,
            email: c.email,
            address: c.address,
            loyaltyPoints: c.loyalty_points ?? 0
          }));
          setCustomers(mapped);
          const walkIn = mapped.find(c =>
            (c.email && c.email.toLowerCase().includes('walkin')) ||
            (c.name && c.name.toLowerCase().includes('walk-in customer'))
          );
          if (walkIn) setCustomer(prev => prev || walkIn);
          console.log('[POSKeyboard] Loaded customers from offline DB:', mapped.length);
        } catch (e2) {
          console.error('Offline customers fallback failed', e2);
        }
      }
    }
  }, []); // Remove customer dependency to ensure it always runs

  // Handle new customer form success
  const handleCustomerFormSuccess = (newCustomer, error) => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    } else {
      // Add the new customer to the list
      setCustomers(prev => [...prev, newCustomer]);

      // Close the dialog
      setAddCustomerDialogOpen(false);

      // Select the new customer
      setCustomer(newCustomer);
      setCustomerInputValue(''); // Clear input value
      setNewCustomerName(''); // Clear pre-filled name

      // Show success message
      enqueueSnackbar(`Customer "${newCustomer.name}" added successfully`, { variant: 'success' });
    }
  };

  // Load locations
  const loadLocations = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/locations');
      const list = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      setLocations(list);
      // Set first location as default if available
      if (list.length > 0 && !selectedLocation) {
        setSelectedLocation(list[0]);
      }
    } catch (e) {
      console.error('Failed to load locations', e);
    }
  }, [selectedLocation]);

  // Refresh customer data for latest loyalty points and balance when selected
  useEffect(() => {
    if (customer?.id && customer.id !== 1) {
      const fetchLatest = async () => {
        try {
          const fresh = await customerService.getCustomerById(customer.id);
          // Only update if it's still the same customer and data is different or points not yet in object
          setCustomer(prev => {
            if (prev?.id === fresh.id) {
              return { ...prev, ...fresh };
            }
            return prev;
          });
        } catch (e) {
          console.error('Failed to fetch fresh customer data', e);
        }
      };
      fetchLatest();
    }
  }, [customer?.id]);

  // Load salesmen for current location (or all) for POS salesman dropdown
  useEffect(() => {
    const loadSalesmen = async () => {
      try {
        const user = getCurrentUser();
        const locationId = user?.location?.id || null;
        const list = await salesmanService.list(locationId != null ? { locationId } : {});
        setSalesmen(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Failed to load salesmen', e);
        if (window.electronAPI?.getOfflineSalesmen) {
          try {
            const offlineList = await window.electronAPI.getOfflineSalesmen(getDesktopOfflineCompanyId());
            setSalesmen(offlineList || []);
            console.log('[POSKeyboard] Loaded salesmen from offline DB:', (offlineList || []).length);
          } catch (e2) {
            setSalesmen([]);
          }
        } else {
          setSalesmen([]);
        }
      }
    };
    loadSalesmen();
  }, []);

  // When payment dialog opens: load loyalty settings and refresh customer for latest points
  useEffect(() => {
    if (!paymentDialogOpen) {
      setRedeemedPoints(0);
      return;
    }
    const load = async () => {
      try {
        const settings = await loyaltySettingsService.get();
        setLoyaltySettings(settings && (settings.minPurchaseAmount != null || settings.amountPerPoint != null) ? settings : null);
      } catch (e) {
        setLoyaltySettings(null);
      }
      if (customer?.id) {
        try {
          const fresh = await customerService.getCustomerById(customer.id);
          setCustomer(fresh);
        } catch (e) { /* keep current customer */ }
      }
    };
    load();
  }, [paymentDialogOpen]);

  // Load print settings (including WhatsApp share) for current location
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Get current user to determine location
        const userStr = localStorage.getItem('user');
        let locationId = null;
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            locationId = user.location?.id;
          } catch (e) {
            console.error('Error parsing user from localStorage', e);
          }
        }

        const settings = await printSettingsService.getPrintSettings(locationId);
        if (settings) {
          if (typeof settings.enableWhatsappShare === 'boolean') {
            setEnableWhatsappShare(settings.enableWhatsappShare);
          }
          if (typeof settings.directPrint === 'boolean') {
            setDirectPrint(settings.directPrint);
          }
          if (typeof settings.previewPrint === 'boolean') {
            setPreviewPrint(settings.previewPrint);
          }
          if (typeof settings.showPrintSchemeSelection === 'boolean') {
            setShowPrintSchemeSelection(settings.showPrintSchemeSelection);
          }
        }
      } catch (error) {
        console.error('Error loading print settings:', error);
        // Don't show error toast, just use default value
      }
    };

    loadSettings();
  }, []);

  // Effective sale rate for current price mode (retailer or wholesale). No wholesale = 0.
  const getSaleRateForMode = useCallback((product) => {
    if (!product) return 0;
    if (priceMode === 'wholesale') {
      const w = product.wholesalePrice ?? product.wholesale_price;
      return (w != null && Number(w) >= 0) ? Number(w) : 0;
    }
    return Number(product.sellingPrice ?? product.salePrice ?? product.unitPrice ?? 0) || 0;
  }, [priceMode]);

  // Get available units for a product (Main Unit, Sub Unit 1, Sub Unit 2). Prices respect priceMode.
  const getAvailableUnits = useCallback((product) => {
    if (!product) return [];

    const mainPrice = getSaleRateForMode(product);
    const availableUnits = [];

    // Main Unit (always available)
    if (product.unit) {
      availableUnits.push({
        id: product.unit.id,
        name: product.unit.name,
        abbreviation: product.unit.abbreviation,
        price: mainPrice,
        type: 'MAIN'
      });
    } else if (product.unitId && units.length > 0) {
      const mainUnit = units.find(u => u.id === product.unitId);
      if (mainUnit) {
        availableUnits.push({
          id: mainUnit.id,
          name: mainUnit.name,
          abbreviation: mainUnit.abbreviation,
          price: mainPrice,
          type: 'MAIN'
        });
      }
    }

    // Sub Unit 1: in wholesale mode we don't have wholesale sub-unit price, use 0
    if (product.subUnit1 && (priceMode === 'retailer' ? product.subUnit1SellingPrice : true)) {
      const sub1Price = priceMode === 'wholesale' ? 0 : (product.subUnit1SellingPrice || 0);
      availableUnits.push({
        id: product.subUnit1.id,
        name: product.subUnit1.name,
        abbreviation: product.subUnit1.abbreviation,
        price: sub1Price,
        type: 'SUB_UNIT1'
      });
    }

    // Sub Unit 2: in wholesale mode we don't have wholesale sub-unit price, use 0
    if (product.subUnit2 && (priceMode === 'retailer' ? product.subUnit2SellingPrice : true)) {
      const sub2Price = priceMode === 'wholesale' ? 0 : (product.subUnit2SellingPrice || 0);
      availableUnits.push({
        id: product.subUnit2.id,
        name: product.subUnit2.name,
        abbreviation: product.subUnit2.abbreviation,
        price: sub2Price,
        type: 'SUB_UNIT2'
      });
    }

    return availableUnits;
  }, [units, priceMode, getSaleRateForMode]);

  /** Left-to-right focusable columns for this row (matches table layout; used for arrow-key grid nav). */
  const getGridNavFieldKeysForRow = useCallback(
    (rowIndex) => {
      const keys = ['itemCode'];
      const item = cart[rowIndex];
      keys.push('qty');
      if (showUom && item?.product) {
        const units = getAvailableUnits(item.product);
        if (units.length > 1) keys.push('uom');
      }
      if (pageFieldConfig.showSaleRate !== false) keys.push('saleRate');
      if (pageFieldConfig.showSaleRateInclusive) keys.push('saleRateInclusive');
      if (showDiscount) {
        keys.push('discountPct');
        keys.push('discount');
      }
      return keys;
    },
    [cart, showUom, showDiscount, pageFieldConfig.showSaleRate, pageFieldConfig.showSaleRateInclusive, getAvailableUnits]
  );

  const handleGridArrowNav = useCallback(
    (e, rowIndex, fieldKey) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      const keys = getGridNavFieldKeysForRow(rowIndex);
      const pos = keys.indexOf(fieldKey);
      if (pos < 0) return;

      const move = (targetKey, targetRow) => {
        e.preventDefault();
        setTimeout(() => focusRef(targetKey, targetRow), 0);
      };

      if (e.key === 'ArrowLeft') {
        if (!posInputAllowsNavOutLeft(e.target)) return;
        if (pos <= 0) return;
        move(keys[pos - 1], rowIndex);
        return;
      }
      if (e.key === 'ArrowRight') {
        if (!posInputAllowsNavOutRight(e.target)) return;
        if (pos >= keys.length - 1) return;
        move(keys[pos + 1], rowIndex);
        return;
      }

      if (e.key === 'ArrowUp') {
        const nextRow = rowIndex - 1;
        if (nextRow < 0) return;
        const tgtKeys = getGridNavFieldKeysForRow(nextRow);
        const targetKey = resolveVerticalNavTarget(fieldKey, keys, tgtKeys);
        move(targetKey, nextRow);
        return;
      }
      if (e.key === 'ArrowDown') {
        const nextRow = rowIndex + 1;
        if (nextRow >= cart.length) return;
        const tgtKeys = getGridNavFieldKeysForRow(nextRow);
        const targetKey = resolveVerticalNavTarget(fieldKey, keys, tgtKeys);
        move(targetKey, nextRow);
        return;
      }
    },
    [getGridNavFieldKeysForRow, focusRef, cart.length]
  );

  /** Product line rate when not using a batch (matches add-to-cart logic incl. Non-GST inclusive rate). */
  const getDefaultProductLinePrice = useCallback(
    (product, selectedUnit) => {
      if (!product) return 0;
      const availableUnits = getAvailableUnits(product);
      const selectedUnitData = selectedUnit
        ? availableUnits.find((u) => u.id === selectedUnit.id) || availableUnits[0]
        : availableUnits[0];
      let basePrice = selectedUnitData
        ? (selectedUnitData.price != null ? Number(selectedUnitData.price) : getSaleRateForMode(product))
        : getSaleRateForMode(product);
      basePrice = Number(basePrice) || 0;
      if (nonGstMode && product?.tax?.rate != null) {
        const taxRate = product.tax.rate / 100;
        basePrice = basePrice * (1 + taxRate);
        basePrice = Math.round(basePrice * 100) / 100;
      }
      return basePrice;
    },
    [getAvailableUnits, getSaleRateForMode, nonGstMode]
  );

  // Search product by code/SKU/barcode
  const searchProduct = useCallback(async (code, rowIndex) => {
    if (!code || code.trim() === '') return;

    try {
      setSearchingProduct(true);

      // Check if user has a location - if yes, search in allProducts (which are location products)
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const userLocationData = user?.location || null;
      const hasLocation = userLocationData && userLocationData.id;

      let product = null;

      if (hasLocation) {
        // Location user: prefer preloaded list; still run API fallbacks when list is empty (e.g. cache miss).
        const query = code.toLowerCase().trim();
        if (allProducts.length > 0) {
          product = allProducts.find(p =>
            (p.sku && p.sku.toLowerCase() === query) ||
            (p.productCode && p.productCode.toLowerCase() === query) ||
            (p.product_code && p.product_code.toLowerCase() === query) ||
            (p.barcode && p.barcode.toLowerCase() === query) ||
            (p.code && p.code.toLowerCase() === query)
          );
        }

        // If not found in list, try barcode lookup then SKU lookup (e.g. scanned barcode or manual SKU entry not in preloaded list)
        if (!product) {
          try {
            const byBarcode = await productService.getProductByBarcode(code.trim());
            if (byBarcode && (!byBarcode.status || byBarcode.status.toUpperCase() === 'ACTIVE')) {
              product = byBarcode;
            }
          } catch (e) {
            // ignore, try SKU next
          }
        }
        if (!product) {
          try {
            const bySku = await productService.getProductBySku(code.trim());
            if (bySku && (!bySku.status || bySku.status.toUpperCase() === 'ACTIVE')) {
              product = bySku;
            }
          } catch (e) {
            // ignore, try searchProducts next
          }
        }
        // Fallback: use backend search (handles partial matches like "e", "phi", "Philips")
        if (!product) {
          try {
            const res = await productService.searchProducts(code.trim());
            const list = (Array.isArray(res) ? res : (res?.content || []))
              .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
            if (list.length > 0) {
              product = list[0];
            }
          } catch (e) {
            // ignore, keep product null
          }
        }

        // If product found but missing sub-unit data, fetch full product details
        if (product && product.id && (!product.subUnit1 && !product.subUnit2)) {
          try {
            const fullProduct = await productService.getProductById(product.id);
            // Merge full product data with location-specific data
            product = {
              ...fullProduct,
              quantity: product.quantity,
              locationProductId: product.locationProductId,
              locationQuantity: product.locationQuantity,
              locationId: product.locationId,
              locationName: product.locationName
            };
          } catch (e) {
            console.warn('Failed to fetch full product details, using location product data:', e);
          }
        }
      } else {
        // Warehouse/Admin user: Search via API (name, SKU, barcode) and filter by active status
        try {
          const res = await productService.searchProducts(code);
          const list = (Array.isArray(res) ? res : (res?.content || []))
            .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');

          if (list.length > 0) {
            product = list[0];
          }
        } catch (searchErr) {
          // searchProducts may return 400 for short keyword (e.g. some barcodes); try barcode lookup
        }

        // Fallback: direct barcode lookup then SKU lookup (for scanned barcode or manual SKU/product code entry)
        if (!product) {
          try {
            const byBarcode = await productService.getProductByBarcode(code.trim());
            if (byBarcode && (!byBarcode.status || byBarcode.status.toUpperCase() === 'ACTIVE')) {
              product = byBarcode;
            }
          } catch (e) {
            // ignore, try SKU next
          }
        }
        if (!product) {
          try {
            const bySku = await productService.getProductBySku(code.trim());
            if (bySku && (!bySku.status || bySku.status.toUpperCase() === 'ACTIVE')) {
              product = bySku;
            }
          } catch (e) {
            // ignore
          }
        }

        // If product found but missing sub-unit data, fetch full product details
        if (product && product.id && (!product.subUnit1 && !product.subUnit2)) {
          try {
            product = await productService.getProductById(product.id);
          } catch (e) {
            console.warn('Failed to fetch full product details, using search result:', e);
          }
        }
      }

      if (product) {
        // Get available units for this product
        const availableUnits = getAvailableUnits(product);

        // Default to Main Unit (first in availableUnits array)
        const defaultUnitData = availableUnits.length > 0 ? availableUnits[0] : null;
        const defaultPrice = defaultUnitData ? defaultUnitData.price : getSaleRateForMode(product);

        // Get default unit object
        let defaultUnit = null;
        if (defaultUnitData) {
          defaultUnit = {
            id: defaultUnitData.id,
            name: defaultUnitData.name,
            abbreviation: defaultUnitData.abbreviation
          };
        } else if (product.unit) {
          defaultUnit = product.unit;
        } else if (product.unitId && units.length > 0) {
          defaultUnit = units.find(u => u.id === product.unitId) || units[0];
        } else if (units.length > 0) {
          defaultUnit = units[0];
        }

        // Auto-apply previous sale price if enabled and customer is not Walk-in (ID 1)
        let finalPrice = defaultPrice;
        if (pageFieldConfig.autoApplyPreviousPrice && customer && customer.id && customer.id !== 1) {
          try {
            const recentSales = await salesService.getRecentProductSales(product.id, customer.id, 1);
            if (recentSales && recentSales.length > 0) {
              const lastSale = recentSales[0];
              if (lastSale.price != null && lastSale.price > 0) {
                finalPrice = lastSale.price;
                const shownAppliedPrice = lastSale.priceWithTax != null ? lastSale.priceWithTax : finalPrice;
                enqueueSnackbar(`Applied previous sale price: ${formatCurrency(finalPrice)}`, {
                  variant: 'info',
                  autoHideDuration: 2000
                });
                if (shownAppliedPrice !== finalPrice) {
                  enqueueSnackbar(`Last sold (with GST): ${formatCurrency(shownAppliedPrice)}`, {
                    variant: 'info',
                    autoHideDuration: 1800
                  });
                }
              }
            }
          } catch (err) {
            console.warn('Failed to fetch previous sale price', err);
          }
        }

        finalPrice = exclusiveToStoredUnitPrice(nonGstMode, product, null, finalPrice);

        const newCart = [...cart];
        newCart[rowIndex] = {
          ...newCart[rowIndex],
          itemCode: product.sku || product.productCode || product.code || code,
          productName: product.name,
          price: finalPrice,
          product: product,
          selectedUnit: defaultUnit,
        };
        setCart(newCart);

        // Move focus to next field in tab order (e.g. Discount if configured), or add new row
        setTimeout(() => {
          const fn = handleTabOrderEnterRef.current;
          if (typeof fn === 'function') {
            fn(rowIndex, 'itemCode');
          } else if (qtyRefs.current[rowIndex]) {
            qtyRefs.current[rowIndex].focus();
            qtyRefs.current[rowIndex].select();
          }
        }, 100);
      } else {
        enqueueSnackbar('Product not found', { variant: 'warning' });
      }
    } catch (e) {
      console.error('Product search failed', e);
      enqueueSnackbar('Failed to search product', { variant: 'error' });
    } finally {
      setSearchingProduct(false);
    }
  }, [cart, enqueueSnackbar, allProducts, units, getAvailableUnits, getSaleRateForMode, nonGstMode, customer, pageFieldConfig.autoApplyPreviousPrice]);

  // Handle item code input change (for autocomplete)
  const handleItemCodeInputChange = async (rowIndex, value) => {
    setCart((prev) => {
      if (!Array.isArray(prev) || !prev[rowIndex]) return prev;
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], itemCode: value };
      return next;
    });

    // If nothing typed, clear options
    if (!value || value.trim().length === 0) {
      setProductOptions((prev) => {
        const next = { ...(prev || {}) };
        next[rowIndex] = [];
        return next;
      });
      return;
    }

    const query = value.toLowerCase();

    try {
      let filtered = [];

      if (allProducts && allProducts.length > 0) {
        // Use already loaded products for fast local filtering
        filtered = allProducts.filter(p =>
          (p.name && p.name.toLowerCase().includes(query)) ||
          (p.sku && p.sku.toLowerCase().includes(query)) ||
          (p.productCode && p.productCode.toLowerCase().includes(query)) ||
          (p.product_code && p.product_code.toLowerCase().includes(query)) ||
          (p.barcode && p.barcode.toLowerCase().includes(query)) ||
          (p.code && p.code.toLowerCase().includes(query))
        ).slice(0, 10);
      } else {
        // Fallback: call backend search if preload failed or returned empty
        console.log('[POSKeyboard] allProducts empty, performing server search for:', value);
        const response = await productService.searchProducts(value);
        const list = Array.isArray(response) ? response : (response?.content || []);
        filtered = list
          .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE')
          .slice(0, 10);
      }

      setProductOptions((prev) => {
        const next = { ...(prev || {}) };
        next[rowIndex] = filtered;
        return next;
      });
      console.log('[POSKeyboard] Autocomplete options for "', value, '":', filtered.length);
    } catch (error) {
      console.error('[POSKeyboard] Autocomplete search failed for value:', value, error);
    }
  };

  // Handle product selection from autocomplete
  const handleProductSelect = async (rowIndex, product) => {
    if (!product) return;

    // If product missing sub-unit data, fetch full product details
    let fullProduct = product;
    if (product.id && (!product.subUnit1 && !product.subUnit2)) {
      try {
        fullProduct = await productService.getProductById(product.id);
        // Preserve location-specific data if it exists
        if (product.locationProductId) {
          fullProduct = {
            ...fullProduct,
            quantity: product.quantity,
            locationProductId: product.locationProductId,
            locationQuantity: product.locationQuantity,
            locationId: product.locationId,
            locationName: product.locationName
          };
        }
      } catch (e) {
        console.warn('Failed to fetch full product details, using autocomplete result:', e);
        fullProduct = product;
      }
    }

    // Get available units for this product
    const availableUnits = getAvailableUnits(fullProduct);

    // Default to Main Unit (first in availableUnits array)
    const defaultUnitData = availableUnits.length > 0 ? availableUnits[0] : null;
    let basePrice = defaultUnitData ? defaultUnitData.price : getSaleRateForMode(fullProduct);
    const productId = fullProduct.id;
    let batches = [];
    let defaultBatch = null;

    if (enableBatchWise) {
      try {
        const locationId = selectedLocation?.id || user?.locationId || null;
        batches = await salesService.getProductBatchesByProduct(productId, locationId);
        batches = (batches || []).filter(isBatchActiveForPos);
        setProductBatchesByProduct((prev) => ({ ...prev, [productId]: batches }));
      } catch (err) {
        console.warn('Failed to load product batches:', err);
        batches = [];
      }

      const inStockBatches = sortBatchesByFefo(
        (batches || []).filter((b) => Number(b?.quantity || 0) > 0 && !isBatchExpiredPos(b?.expiryDate))
      );
      const allSellableBatches = sortBatchesByFefo(
        (batches || []).filter((b) => !isBatchExpiredPos(b?.expiryDate))
      );
      defaultBatch = inStockBatches[0] || allSellableBatches[0] || null;
    }

    // Get default unit object
    let defaultUnit = null;
    if (defaultUnitData) {
      defaultUnit = {
        id: defaultUnitData.id,
        name: defaultUnitData.name,
        abbreviation: defaultUnitData.abbreviation
      };
    } else if (fullProduct.unit) {
      defaultUnit = fullProduct.unit;
    } else if (fullProduct.unitId && units.length > 0) {
      defaultUnit = units.find(u => u.id === fullProduct.unitId) || units[0];
    } else if (units.length > 0) {
      defaultUnit = units[0];
    }

    // Auto-apply previous sale price if enabled and customer is not Walk-in (ID 1)
    if (pageFieldConfig.autoApplyPreviousPrice && customer && customer.id && customer.id !== 1) {
      try {
        const recentSales = await salesService.getRecentProductSales(productId, customer.id, 1);
        if (recentSales && recentSales.length > 0) {
          const lastSale = recentSales[0];
          if (lastSale.price != null && lastSale.price > 0) {
            basePrice = lastSale.price;
            const shownAppliedPrice = lastSale.priceWithTax != null ? lastSale.priceWithTax : basePrice;
            enqueueSnackbar(`Applied previous sale price: ${formatCurrency(basePrice)}`, {
              variant: 'info',
              autoHideDuration: 2000
            });
            if (shownAppliedPrice !== basePrice) {
              enqueueSnackbar(`Last sold (with GST): ${formatCurrency(shownAppliedPrice)}`, {
                variant: 'info',
                autoHideDuration: 1800
              });
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch previous sale price', err);
      }
    }

    let focusAfter = null; // { type: 'itemCode'|'tabOrder'|'itemCodeRow', rowIndex: number }

    setCart((prevCart) => {
      const safeCart = Array.isArray(prevCart) ? prevCart : [];
      const nextCart = [...safeCart];
      if (!nextCart[rowIndex]) return prevCart;

      // Check if this product already exists in the cart (any row)
      const existingProductIndex = nextCart.findIndex((item) =>
        item.product &&
        item.product.id === productId &&
        (item.productBatchId || null) === (defaultBatch?.id || null)
      );

      if (existingProductIndex !== -1) {
        const currentItem = nextCart[existingProductIndex];
        const available = getAvailableStock(currentItem);
        const allowNegative = pageFieldConfig.allowNegativeStock;

        if (!allowNegative && available !== undefined && available !== null && (lineItemQty(currentItem) + 1) > available) {
          enqueueSnackbar(`Low Stock: Only ${available} available`, { variant: 'warning' });
          // If scan happened in a different row, clear that scanning row
          if (rowIndex !== existingProductIndex && nextCart[rowIndex]) {
            nextCart[rowIndex] = {
              ...nextCart[rowIndex],
              itemCode: '',
              productName: '',
              price: 0,
              discount: 0,
              discountPercentage: 0,
              discountAmount: 0,
              product: null,
              selectedUnit: null,
              productBatchId: null,
              batchNumber: null,
              expiryDate: null,
              mfd: null,
              selectedBatch: null,
            };
          }
          focusAfter = { type: 'itemCodeRow', rowIndex };
        } else {
          nextCart[existingProductIndex] = { ...currentItem, quantity: lineItemQty(currentItem) + 1 };

          // Move existing product row to last so user sees the updated qty
          const itemToMove = nextCart[existingProductIndex];
          nextCart.splice(existingProductIndex, 1);
          nextCart.push(itemToMove);

          // If scan happened in a different row, remove that row then add one empty row at end
          if (rowIndex !== existingProductIndex) {
            const removeIndex = rowIndex < existingProductIndex ? rowIndex : rowIndex - 1;
            if (removeIndex >= 0 && removeIndex < nextCart.length) {
              nextCart.splice(removeIndex, 1);
            }
            const newId = nextCart.length > 0 ? Math.max(...nextCart.map(c => c.id)) + 1 : 1;
            nextCart.push({
              id: newId,
              itemCode: '',
              productName: '',
              quantity: 1,
              price: 0,
              discount: 0,
              discountPercentage: 0,
              discountAmount: 0,
              product: null,
              selectedUnit: null,
              productBatchId: null,
              batchNumber: null,
              expiryDate: null,
              mfd: null,
              selectedBatch: null,
            });
          }

          const lastIndex = nextCart.length - 1;
          setSelectedRowIndex(lastIndex);
          focusAfter = { type: 'itemCodeRow', rowIndex: lastIndex };
        }
      } else {
        // New product - add it to the current row
        let finalPrice = basePrice;
        if (nonGstMode && fullProduct?.tax?.rate != null) {
          const taxRate = fullProduct.tax.rate / 100;
          finalPrice = basePrice * (1 + taxRate);
          finalPrice = Math.round(finalPrice * 100) / 100;
        }

        const batchExclusive =
          defaultBatch?.sellingPrice != null && defaultBatch.sellingPrice !== ''
            ? Number(defaultBatch.sellingPrice)
            : null;
        const newItem = {
          ...nextCart[rowIndex],
          itemCode: fullProduct.sku || fullProduct.name,
          productName: fullProduct.name,
          price:
            batchExclusive != null
              ? exclusiveToStoredUnitPrice(nonGstMode, fullProduct, defaultBatch, batchExclusive)
              : finalPrice,
          discount: 0,
          discountPercentage: 0,
          discountAmount: 0,
          product: fullProduct,
          selectedUnit: defaultUnit,
          quantity: 1,
          productBatchId: defaultBatch?.id || null,
          batchNumber: defaultBatch?.batchNumber || null,
          expiryDate: defaultBatch?.expiryDate || null,
          mfd: defaultBatch?.mfd || defaultBatch?.manufacturingDate || null,
          selectedBatch: defaultBatch || null
        };

        const available = getAvailableStock(newItem);
        const allowNegative = pageFieldConfig.allowNegativeStock;
        if (!allowNegative && available !== undefined && available !== null && 1 > available) {
          enqueueSnackbar(`Out of Stock: Product has ${available} available`, { variant: 'warning' });
        }

        nextCart[rowIndex] = newItem;
        focusAfter = { type: 'tabOrder', rowIndex };
      }

      return nextCart;
    });

    // Post-update focus: keep this outside setCart to avoid racing React state batching
    setTimeout(() => {
      if (!focusAfter) return;
      if (focusAfter.type === 'itemCodeRow') {
        const ri = focusAfter.rowIndex;
        itemCodeRefs.current[ri]?.focus?.();
        itemCodeRefs.current[ri]?.select?.();
        return;
      }
      if (focusAfter.type === 'tabOrder') {
        const fn = handleTabOrderEnterRef.current;
        if (typeof fn === 'function') {
          fn(focusAfter.rowIndex, 'itemCode');
        } else {
          qtyRefs.current[focusAfter.rowIndex]?.focus?.();
          qtyRefs.current[focusAfter.rowIndex]?.select?.();
        }
      }
    }, 120);

    // Item history: only if enabled in settings
    if (pageFieldConfig.showItemHistory && fullProduct?.id != null && customer?.id != null) {
      setProductHistoryTab('productHistory');
      loadProductHistory(fullProduct, customer);
    }
  };

  // Handle item code blur (search product)
  const handleItemCodeBlur = (rowIndex, value) => {
    if (value && !cart[rowIndex].product) {
      searchProduct(value, rowIndex);
    }
  };

  // Handle quantity change (decimals allowed, e.g. 1.5 kg — aligned with sale rate parsing)
  const handleQuantityChange = (rowIndex, value) => {
    const newCart = [...cart];
    const row = { ...newCart[rowIndex] };

    if (value === '' || value == null) {
      row.quantity = '';
      newCart[rowIndex] = row;
      setCart(newCart);
      return;
    }
    const allowDecimalQty = Boolean(pageFieldConfig.allowDecimalQty);
    let cleaned = String(value).replace(allowDecimalQty ? /[^\d.]/g : /[^\d]/g, '');
    if (allowDecimalQty) {
      const dot = cleaned.indexOf('.');
      if (dot !== -1) {
        cleaned = cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
      }
    }
    if (cleaned === '' || cleaned === '.') {
      row.quantity = '';
      newCart[rowIndex] = row;
      setCart(newCart);
      return;
    }
    // Keep "1." as a string while the user types the fraction. If we parse→number here,
    // controlled input re-renders as "1" and the next digit becomes "15" instead of "1.5".
    if (allowDecimalQty && /^\d+\.$/.test(cleaned)) {
      row.quantity = cleaned;
      newCart[rowIndex] = row;
      setCart(newCart);
      return;
    }
    const parsed = allowDecimalQty ? parseFloat(cleaned) : parseInt(cleaned, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      row.quantity = '';
      newCart[rowIndex] = row;
      setCart(newCart);
      return;
    }
    let newQty = allowDecimalQty ? roundPosQty(parsed) : parsed;

    if (row.product) {
      const tempItem = { ...row, quantity: newQty };
      const available = getAvailableStock(tempItem);
      const allowNegative = pageFieldConfig.allowNegativeStock;
      const availNum = available != null ? Number(available) : null;
      if (!allowNegative && availNum != null && Number.isFinite(availNum) && newQty > availNum) {
        enqueueSnackbar(`Low Stock: Only ${available} available`, { variant: 'warning' });
        newQty = availNum > 0
          ? (allowDecimalQty ? roundPosQty(availNum) : Math.floor(availNum))
          : 1;
      }
    }

    row.quantity = newQty;
    newCart[rowIndex] = row;
    setCart(newCart);
  };

  // Handle price change (text field: allow clear; parseFloat removes leading zeros like "089" -> 89)
  const handlePriceChange = (rowIndex, value) => {
    const newCart = [...cart];
    const item = { ...newCart[rowIndex] };
    delete item.price_inclusive_input; // Clear inclusive input when editing exclusive price

    if (value === '' || value == null) {
      newCart[rowIndex] = { ...item, price: '' };
      setCart(newCart);
      return;
    }
    const cleaned = String(value).replace(/[^\d.]/g, '');
    if (cleaned === '' || cleaned === '.') {
      newCart[rowIndex] = { ...item, price: '' };
      setCart(newCart);
      return;
    }
    const num = parseFloat(cleaned);
    if (!Number.isFinite(num)) {
      newCart[rowIndex] = { ...item, price: '' };
      setCart(newCart);
      return;
    }
    newCart[rowIndex] = { ...item, price: num };
    setCart(newCart);
  };

  // Handle inclusive price change (allows editing price with GST)
  const handleInclusivePriceChange = (rowIndex, value) => {
    const newCart = [...cart];
    const item = { ...newCart[rowIndex] };
    item.price_inclusive_input = value; // Store the raw input value to prevent rounding jumps

    if (value === '' || value == null) {
      item.price = '';
      newCart[rowIndex] = item;
      setCart(newCart);
      return;
    }
    const cleaned = String(value).replace(/[^\d.]/g, '');
    if (cleaned === '' || cleaned === '.') {
      item.price = '';
      newCart[rowIndex] = item;
      setCart(newCart);
      return;
    }
    const inclusivePrice = parseFloat(cleaned);
    if (!Number.isFinite(inclusivePrice)) {
      item.price = '';
      newCart[rowIndex] = item;
      setCart(newCart);
      return;
    }

    if (nonGstMode) {
      // In non-GST mode, the price IS already inclusive.
      item.price = inclusivePrice;
    } else {
      // In GST mode, back-calculate the exclusive price.
      const taxRate = (item.selectedBatch?.tax?.rate ?? item.product?.tax?.rate ?? 0) / 100;
      const exclusivePrice = inclusivePrice / (1 + taxRate);
      item.price = Number(exclusivePrice.toFixed(4));
    }
    newCart[rowIndex] = item;
    setCart(newCart);
  };

  // Handle overall discount change with bidirectional conversion
  const handleOverallDiscountChange = (value, type) => {
    const totals = calculateTotals();
    const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const baseForOverallDiscount = nonGstMode ? totals.subtotal : (totals.subtotal + totals.taxAmount - itemDiscounts);

    setDiscountType(type === 'percentage' ? 'PERCENTAGE' : 'AMOUNT');

    let discountPercent = 0;
    let discountAmt = 0;

    if (value === '' || value === null || value === undefined) {
      setOverallDiscountPercentage(0);
      setDiscount(0);
    } else {
      const numValue = parseFloat(value) || 0;

      if (type === 'percentage') {
        discountPercent = Math.min(numValue, 100);
        discountAmt = baseForOverallDiscount * (discountPercent / 100);

        setOverallDiscountPercentage(numValue); // Store exact value typed
        setDiscount(Math.round(discountAmt * 100) / 100);
      } else {
        discountAmt = Math.min(numValue, baseForOverallDiscount);
        discountPercent = baseForOverallDiscount > 0 ? (discountAmt / baseForOverallDiscount) * 100 : 0;

        setDiscount(numValue); // Store exact value typed
        setOverallDiscountPercentage(Math.round(discountPercent * 100) / 100);
      }
    }
  };

  // Handle discount change with bidirectional conversion
  const handleItemDiscountChange = (rowIndex, value, type) => {
    const newCart = [...cart];
    const item = newCart[rowIndex];
    const baseAmount = lineItemQty(item) * lineUnitPrice(item);

    let discountPercent = 0;
    let discountAmt = 0;

    // If field is empty, set to 0
    if (value === '' || value === null || value === undefined) {
      discountPercent = 0;
      discountAmt = 0;
    } else {
      const numValue = parseFloat(value) || 0;

      if (type === 'percentage') {
        // User entered percentage - calculate amount
        discountPercent = Math.min(numValue, 100); // Cap at 100%
        discountAmt = baseAmount * (discountPercent / 100);
      } else {
        // User entered amount - calculate percentage
        discountAmt = Math.min(numValue, baseAmount); // Cap at base amount
        discountPercent = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
      }
    }

    newCart[rowIndex].discountPercentage = Math.round(discountPercent * 100) / 100;
    newCart[rowIndex].discountAmount = Math.round(discountAmt * 100) / 100;
    newCart[rowIndex].discount = discountAmt; // Keep for backward compatibility

    setCart(newCart);
  };



  // Helper: get available stock for a cart row in the same UOM as the row's selected unit.
  // Stock is always stored in main unit; we convert to selected unit so quantity vs available comparison is correct.
  const getAvailableStock = (item) => {
    if (!item || !item.product) return 0;
    if (item.selectedBatch && isBatchExpiredPos(item.selectedBatch.expiryDate)) {
      return 0;
    }
    const p = item.product;
    // Repacked products now have independent stock — treat them the same as normal products.
    // Available stock is always in main unit (e.g. 50 kg)
    const batchQty = item?.selectedBatch?.quantity;
    const availableMain = batchQty != null
      ? Number(batchQty)
      : (typeof p.locationQuantity === 'number'
        ? p.locationQuantity
        : (typeof p.quantity === 'number' ? p.quantity : Number(p.quantity)));
    if (availableMain == null || isNaN(availableMain)) return 0;

    const selectedUnitId = item.selectedUnit?.id != null ? Number(item.selectedUnit.id) : null;
    const mainUnitId = p.unit?.id != null ? Number(p.unit.id) : (p.unitId != null ? Number(p.unitId) : null);
    const sub1Id = p.subUnit1?.id != null ? Number(p.subUnit1.id) : null;
    const sub2Id = p.subUnit2?.id != null ? Number(p.subUnit2.id) : null;

    // No unit selected or main unit selected: return as-is (main unit)
    if (selectedUnitId == null || selectedUnitId === mainUnitId) {
      return availableMain;
    }

    // Sub Unit 1: 1 main = subUnit1ConversionRatio sub1 (e.g. 1 kg = 1000 g)
    const ratio1 = p.subUnit1ConversionRatio != null ? Number(p.subUnit1ConversionRatio) : 0;
    if (sub1Id != null && selectedUnitId === sub1Id && ratio1 > 0) {
      return availableMain * ratio1;
    }

    // Sub Unit 2: 1 main = ratio1 * ratio2 sub2
    const ratio2 = p.subUnit2ConversionRatio != null ? Number(p.subUnit2ConversionRatio) : 0;
    if (sub2Id != null && selectedUnitId === sub2Id && ratio1 > 0 && ratio2 > 0) {
      return availableMain * ratio1 * ratio2;
    }

    return availableMain;
  };

  // Clear selected product for a row and reset inputs
  const clearRowProduct = (rowIndex) => {
    const newCart = [...cart];
    if (!newCart[rowIndex]) return;
    newCart[rowIndex] = {
      ...newCart[rowIndex],
      itemCode: '',
      productName: '',
      price: 0,
      discount: 0,
      discountPercentage: 0,
      discountAmount: 0,
      product: null,
      selectedUnit: null,
      productBatchId: null,
      batchNumber: null,
      expiryDate: null,
      mfd: null,
      selectedBatch: null
    };
    setCart(newCart);
    const newOptions = { ...productOptions };
    newOptions[rowIndex] = [];
    setProductOptions(newOptions);
    setTimeout(() => {
      itemCodeRefs.current[rowIndex]?.focus();
    }, 100);
  };

  const applyBatchChange = useCallback(
    (rowIndex, batchIdValue) => {
      setCart((prev) => {
        const newCart = [...prev];
        const item = { ...newCart[rowIndex] };
        if (!item.product) return prev;

        const batchId = batchIdValue === '' || batchIdValue == null ? null : Number(batchIdValue);
        const productId = item.product.id;
        const batches = productBatchesByProduct[productId] || [];
        const selectedBatch =
          batchId != null && !Number.isNaN(batchId) ? batches.find((b) => b.id === batchId) || null : null;

        if (selectedBatch != null && isBatchExpiredPos(selectedBatch.expiryDate)) {
          setTimeout(() => {
            enqueueSnackbar(
              `Batch ${selectedBatch.batchNumber} has expired (exp. ${formatExpiryForPos(selectedBatch.expiryDate)}). It cannot be sold.`,
              { variant: 'error' }
            );
          }, 0);
          return prev;
        }

        item.productBatchId = selectedBatch?.id || null;
        item.batchNumber = selectedBatch?.batchNumber || null;
        item.expiryDate = selectedBatch?.expiryDate || null;
        item.mfd = selectedBatch?.mfd || selectedBatch?.manufacturingDate || null;
        item.selectedBatch = selectedBatch;

        if (selectedBatch != null && selectedBatch.sellingPrice != null && selectedBatch.sellingPrice !== '') {
          item.price = exclusiveToStoredUnitPrice(
            nonGstMode,
            item.product,
            selectedBatch,
            Number(selectedBatch.sellingPrice)
          );
        } else {
          item.price = getDefaultProductLinePrice(item.product, item.selectedUnit);
        }

        const available = getAvailableStock(item);
        const allowNegative = pageFieldConfig.allowNegativeStock;
        if (!allowNegative && available !== undefined && available !== null && lineItemQty(item) > available) {
          setTimeout(() => {
            enqueueSnackbar(`Low Stock in selected batch: Only ${available} available`, { variant: 'warning' });
          }, 0);
          const av = Number(available);
          item.quantity = av > 0 ? roundPosQty(av) : 1;
        }

        newCart[rowIndex] = item;
        return newCart;
      });
    },
    [
      productBatchesByProduct,
      getDefaultProductLinePrice,
      pageFieldConfig.allowNegativeStock,
      enqueueSnackbar,
      getAvailableStock,
      nonGstMode,
    ]
  );

  const handleBatchChange = (rowIndex, batchIdValue) => {
    const item = cart[rowIndex];
    if (!item?.product) return;
    const batches = productBatchesByProduct[item.product.id] || [];

    if (batchIdValue === '' || batchIdValue == null) {
      applyBatchChange(rowIndex, '');
      return;
    }

    const batchId = Number(batchIdValue);
    if (Number.isNaN(batchId)) return;

    const chosenPre = batches.find((b) => b.id === batchId);
    if (chosenPre && isBatchExpiredPos(chosenPre.expiryDate)) {
      enqueueSnackbar(
        `Batch ${chosenPre.batchNumber} has expired (exp. ${formatExpiryForPos(chosenPre.expiryDate)}). Choose another batch or No batch.`,
        { variant: 'error' }
      );
      return;
    }

    const sooner = getSoonestInStockBatch(batches);
    const chosen = batches.find((b) => b.id === batchId);
    if (sooner && chosen && sooner.id !== chosen.id) {
      const tSoon = parseExpiryTimestamp(sooner.expiryDate);
      const tChosen = parseExpiryTimestamp(chosen.expiryDate);
      if (tSoon != null && tChosen != null && tSoon < tChosen) {
        setBatchEarlierExpiryDialog({
          rowIndex,
          pendingBatchIdValue: String(batchIdValue),
          soonerBatch: sooner,
          chosenBatch: chosen,
        });
        return;
      }
    }

    applyBatchChange(rowIndex, String(batchIdValue));
  };

  // Handle UOM change
  const handleUOMChange = (rowIndex, unitId) => {
    const newCart = [...cart];
    const item = newCart[rowIndex];

    if (!item.product) return;

    // Get available units for this product
    const availableUnits = getAvailableUnits(item.product);
    const selectedUnitData = availableUnits.find((u) => String(u.id) === String(unitId));

    if (selectedUnitData) {
      // Update selected unit and price
      newCart[rowIndex].selectedUnit = {
        id: selectedUnitData.id,
        name: selectedUnitData.name,
        abbreviation: selectedUnitData.abbreviation
      };
      newCart[rowIndex].price = nonGstMode
        ? getDefaultProductLinePrice(item.product, newCart[rowIndex].selectedUnit)
        : (selectedUnitData.price ?? getSaleRateForMode(item.product));
      setCart(newCart);
    } else {
      // Fallback: if unit not found in available units, use main unit price for current mode
      const selectedUnit = units.find((u) => String(u.id) === String(unitId));
      if (selectedUnit) {
        newCart[rowIndex].selectedUnit = selectedUnit;
        newCart[rowIndex].price = nonGstMode
          ? getDefaultProductLinePrice(item.product, selectedUnit)
          : getSaleRateForMode(item.product);
        setCart(newCart);
      }
    }
  };

  // Add new row
  const addNewRow = useCallback(() => {
    // Reuse existing empty row if present (avoid creating duplicate blank rows).
    const existingEmptyIndex = cart.findIndex((r) =>
      !r?.product &&
      (!r?.itemCode || String(r.itemCode).trim() === '') &&
      (!r?.productName || String(r.productName).trim() === '')
    );
    if (existingEmptyIndex >= 0) {
      setTimeout(() => {
        if (itemCodeRefs.current[existingEmptyIndex]) {
          itemCodeRefs.current[existingEmptyIndex].focus();
        }
      }, 100);
      return;
    }

    const newId = cart.length > 0 ? Math.max(...cart.map(c => c.id)) + 1 : 1;
    setCart([...cart, {
      id: newId,
      itemCode: '',
      productName: '',
      quantity: 1,
      price: 0,
      discount: 0,
      discountPercentage: 0,
      discountAmount: 0,
      product: null,
      selectedUnit: null,
      productBatchId: null,
      batchNumber: null,
      expiryDate: null,
      mfd: null,
      selectedBatch: null
    }]);

    // Focus on new row's item code
    setTimeout(() => {
      const newIndex = cart.length;
      if (itemCodeRefs.current[newIndex]) {
        itemCodeRefs.current[newIndex].focus();
      }
    }, 100);
  }, [cart]);

  const handleTabOrderEnter = useCallback((rowIndex, currentKey) => {
    const currentRow = cart[rowIndex];
    // Never advance tab-order (or create next row) until a product is selected in this row.
    if (!currentRow?.product) {
      return;
    }

    const rowKeys = getGridNavFieldKeysForRow(rowIndex);
    const activeKeys = orderedFieldKeys.filter((k) => rowKeys.includes(k));
    let idx = activeKeys.indexOf(currentKey);

    // UOM can be missing from saved focusOrder (older configs) but is still a real grid step — advance using row layout.
    if (currentKey === 'uom' && idx < 0 && rowKeys.includes('uom')) {
      const uomPos = rowKeys.indexOf('uom');
      const nextKey = uomPos >= 0 && uomPos < rowKeys.length - 1 ? rowKeys[uomPos + 1] : null;
      if (nextKey) {
        setTimeout(() => focusRef(nextKey, rowIndex), 50);
      } else {
        addNewRow();
      }
      return;
    }

    if (idx < 0) return;
    if (idx + 1 < activeKeys.length) {
      const nextKey = activeKeys[idx + 1];
      setTimeout(() => focusRef(nextKey, rowIndex), 50);
      return;
    }
    addNewRow();
  }, [orderedFieldKeys, focusRef, addNewRow, cart, getGridNavFieldKeysForRow]);

  handleTabOrderEnterRef.current = handleTabOrderEnter;

  // Delete row
  const deleteRow = useCallback((rowIndex) => {
    if (cart.length === 1) {
      // Reset the only row
      setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null, productBatchId: null, batchNumber: null, expiryDate: null, mfd: null, selectedBatch: null }]);
    } else {
      setCart(prevCart => prevCart.filter((_, idx) => idx !== rowIndex));
    }
  }, [cart]);

  // Clear all
  const clearAll = useCallback(() => {
    setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null, productBatchId: null, batchNumber: null, expiryDate: null, mfd: null, selectedBatch: null }]);
    setDiscount(0);
    setOverallDiscountPercentage(0);
    setNotes('');
    setReceivedAmount('');

    // Reset customer to Walk-in Customer (Electron: clear first, then reload catalogs so offline lists replace stale API data)
    if (typeof window !== 'undefined' && window.electronAPI) {
      setCustomer(null);
    } else {
      const walkInCustomer = customers.find(c =>
        (c.email && c.email.toLowerCase().includes('walkin')) ||
        (c.name && c.name.toLowerCase().includes('walk-in customer')) ||
        (c.customerId === '0000000000' || c.customerId === 0)
      );
      if (walkInCustomer) {
        setCustomer(walkInCustomer);
      } else {
        setCustomer(null);
      }
    }

    // Reset autocomplete options and other search states
    setProductOptions({});

    // Reload recent orders to show the latest transaction
    loadRecentOrders();
    refreshNextOrderNumber();

    // After clearing (successful bill / reset), re-apply POS initial focus. Multiple passes fix
    // focus lost after native window.confirm() (e.g. offline print prompt) and React ref timing.
    initialFocusAppliedRef.current = true;
    const refocus = () => applyConfiguredInitialFocus({ force: true });
    refocus();
    requestAnimationFrame(() => {
      requestAnimationFrame(refocus);
    });
    setTimeout(refocus, 120);
    setTimeout(refocus, 320);

    if (typeof window !== 'undefined' && window.electronAPI) {
      Promise.resolve().then(async () => {
        await loadAllProducts();
        await loadCustomers();
        refocus();
        setTimeout(refocus, 120);
      });
    }
  }, [customers, setCustomer, loadRecentOrders, refreshNextOrderNumber, applyConfiguredInitialFocus, loadAllProducts, loadCustomers]);

  // Handle hold bill success
  const handleHoldBillSuccess = useCallback(() => {
    enqueueSnackbar('Bill held successfully', { variant: 'success' });
    clearAll();
  }, [clearAll, enqueueSnackbar]);

  // Handle processing a hold bill
  const handleProcessHoldBill = async (bill) => {
    try {
      // If there is already an unbilled cart with real items, confirm before replacing it
      const hasExistingItems = cart.some((ci) => ci.product && lineItemQty(ci) > 0);
      if (hasExistingItems) {
        const proceed = window.confirm(
          'Current bill is not yet billed. Do you want to discard it and load the selected hold bill?'
        );
        if (!proceed) {
          return; // Keep current cart as-is
        }
      }

      // Load items into cart
      const newCart = bill.items.map((item, index) => ({
        id: index + 1,
        itemCode: item.itemCode || '',
        productName: item.productName,
        quantity: normalizeLoadedLineQuantity(item.quantity),
        price: item.unitPrice,
        product: {
          id: item.productId,
          name: item.productName,
          sku: item.itemCode || null,
          barcode: item.itemCode || null,
          sellingPrice: item.unitPrice,
          salePrice: item.unitPrice,
          tax: item.taxRate != null ? { rate: Number(item.taxRate) } : null
        },
        selectedUnit: null,
        productBatchId: enableBatchWise ? (item.productBatchId || null) : null,
        batchNumber: enableBatchWise ? (item.batchNumber || null) : null,
        expiryDate: enableBatchWise ? (item.expiryDate || null) : null,
        mfd: enableBatchWise ? (item.mfd || null) : null,
        selectedBatch: null
      }));

      setCart(newCart);

      // Set customer
      if (bill.customerId) {
        // Try to find the full customer object if needed, but for now just set what we have
        setCustomer({
          id: bill.customerId,
          name: bill.customerName
        });
      }

      // Delete the hold bill since it's now in the cart
      await holdBillService.deleteHoldBill(bill.id);

      enqueueSnackbar('Hold bill loaded successfully', { variant: 'success' });
      setViewHoldBillsModalOpen(false); // Close the view hold bills modal
    } catch (error) {
      console.error('Error processing hold bill:', error);
      enqueueSnackbar('Failed to load hold bill', { variant: 'error' });
    }
  };

  // Handle payment
  // paymentMethodOverride: when provided (JSON string from Split Bill), use it instead of paymentMethod state
  const handlePayment = useCallback(async (paymentMethodOverride = null) => {
    const validItems = cart.filter((item) => item.product && lineItemQty(item) > 0);

    if (validItems.length === 0) {
      enqueueSnackbar('Please add at least one item', { variant: 'warning' });
      return;
    }

    const expiredBatchLine = validItems.find(
      (ci) =>
        enableBatchWise &&
        ci.productBatchId &&
        (isBatchExpiredPos(ci.expiryDate) || isBatchExpiredPos(ci.selectedBatch?.expiryDate))
    );
    if (expiredBatchLine) {
      const bn = expiredBatchLine.batchNumber || expiredBatchLine.selectedBatch?.batchNumber || '';
      const exp = formatExpiryForPos(expiredBatchLine.expiryDate || expiredBatchLine.selectedBatch?.expiryDate);
      enqueueSnackbar(
        `Batch ${bn} has expired (exp. ${exp}). Change batch or use No batch before billing.`,
        { variant: 'error' }
      );
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const companyId = user?.company?.id || user?.companyId || 1;
      const totals = calculateTotals();

      const items = validItems.map((ci) => ({
        productId: ci.product.id,
        quantity: lineItemQty(ci),
        unitPrice: lineUnitPrice(ci),
        taxId: nonGstMode ? null : (ci.selectedBatch?.tax?.id || ci.product?.tax?.id || null),
        discount: Number(ci.discount || 0),
        unitId: ci.selectedUnit?.id || null, // Send the selected unit ID
        productBatchId: enableBatchWise ? (ci.productBatchId || null) : null,
      }));

      // Use override (split bill JSON) or regular payment method
      const backendPaymentMethod = paymentMethodOverride != null ? paymentMethodOverride : paymentMethod;

      // Determine payment status: split with credit -> PENDING, else PAID
      let isCredit = backendPaymentMethod === 'CREDIT';
      let isImmediatePayment = ['CASH', 'CREDIT_CARD', 'PAYNOW'].includes(backendPaymentMethod);
      if (typeof backendPaymentMethod === 'string' && backendPaymentMethod.startsWith('{')) {
        try {
          const split = JSON.parse(backendPaymentMethod);
          isCredit = split?.payments?.some((p) => p.mode === 'CREDIT') || false;
          isImmediatePayment = !isCredit;
        } catch (e) {
          isCredit = false;
          isImmediatePayment = true;
        }
      }
      const paymentStatus = isImmediatePayment ? 'PAID' : (isCredit ? 'PENDING' : 'PAID');

      // For admin users (no location assigned), locationId should be null
      // For location users, use their assigned location
      const userLocationId = user?.location?.id || user?.locationId || null;
      // Only set locationId if user has a location (location users), otherwise null for admin
      const locationId = userLocationId || null;

      // Validate credit sale (single CREDIT or split with CREDIT)
      if (isCredit && (!customer || !customer.id)) {
        alert('Customer selection is required for credit sales');
        return;
      }

      // When round-off is enabled, send rounded total + roundOffAmount so backend stores them (DB total = rounded; bill print shows it)
      const sendRoundOff = roundOffEnabled && totals.roundOff != null;

      const request = {
        customerId: isCredit ? customer.id : (customer?.id ?? null),
        orderDate: new Date().toISOString().slice(0, 10),
        companyId,
        items,
        paymentMethod: backendPaymentMethod, // Use backend expected payment method
        paymentStatus: paymentStatus,
        status: isQuotationMode ? 'PENDING' : 'DELIVERED', // Quotations should be PENDING, not DELIVERED
        notes: notes || 'POS sale',
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        total: totals.total, // When round-off enabled this is the rounded amount (e.g. 174)
        discount: discount,
        shippingCost: 0,
        locationId: locationId, // Send location ID for location-based sales
        salesmanId: selectedSalesmanId ? parseInt(selectedSalesmanId, 10) : null, // Optional: attribute to salesman
        updateCreditBalance: false, // Don't update credit balance here
        isQuotation: isQuotationMode, // Add quotation flag
        nonGst: nonGstMode, // Non-GST bill: tax-inclusive rate, no GST displayed
        redeemedPoints: redeemedPoints > 0 ? redeemedPoints : null, // Loyalty points redeemed on this bill
        ...(sendRoundOff && { roundOffAmount: totals.roundOff }), // Backend stores total + round_off_amount; bill print uses rounded total
      };

      console.log('[POSKeyboard] Creating/updating sales order with data:', {
        paymentMethod: backendPaymentMethod,
        paymentStatus,
        total: totals.total,
        locationId,
        itemsCount: items.length,
        customerId: request.customerId
      });

      // Decide whether to create, convert quotation, or update existing order
      let result;
      try {
        if (quotationId) {
          // Convert quotation to bill
          result = await salesService.convertQuotationToBill(quotationId, request);
          setQuotationId(null);
          enqueueSnackbar('Quotation converted to bill successfully!', { variant: 'success' });
          setIsQuotationMode(false);
        } else if (editOrderId) {
          // Update existing sales order
          result = await salesService.updateSalesOrder(editOrderId, request);
          enqueueSnackbar('Order updated successfully!', { variant: 'success' });
          setEditOrderId(null);
          setIsQuotationMode(false);
        } else {
          // Create new sales order
          result = await salesService.createSalesOrder(request);
          const successMsg = isQuotationMode ? 'Quotation created successfully!' : 'Sale completed successfully!';
          enqueueSnackbar(successMsg, { variant: 'success' });
          if (isQuotationMode) setIsQuotationMode(false);
        }
      } catch (apiErr) {
        // Desktop offline fallback: save to local SQLite when API fails (network down)
        if (window.electronAPI && !quotationId && !editOrderId && !isQuotationMode) {
          try {
            const offlineResult = await window.electronAPI.saveOfflineOrder(request);
            enqueueSnackbar(`Offline: Bill saved (${offlineResult.localInvoiceNo}). Will sync when online.`, { variant: 'success' });
            setPaymentDialogOpen(false);
            const fakeOrder = { id: offlineResult.localId, orderNumber: offlineResult.localInvoiceNo };
            setLastCreatedOrder(fakeOrder);
            const user = JSON.parse(localStorage.getItem('user')) || {};
            const pdfBytes = generateOfflineReceiptPdf({
              cartItems: validItems,
              customer,
              totals,
              localInvoiceNo: offlineResult.localInvoiceNo,
              paymentMethod: backendPaymentMethod || paymentMethod,
              notes: notes || '',
              company: user?.company || {}
            });
            if (window.electronAPI.printPdf) {
              if (directPrint) {
                const defaultPrinter = await window.electronAPI.getDefaultPrinter();
                await window.electronAPI.printPdf(Array.from(pdfBytes), {
                  deviceName: defaultPrinter || undefined,
                  title: `POS Receipt ${offlineResult.localInvoiceNo}`
                });
              } else if (window.confirm('Print offline receipt?')) {
                const defaultPrinter = await window.electronAPI.getDefaultPrinter();
                await window.electronAPI.printPdf(Array.from(pdfBytes), {
                  deviceName: defaultPrinter || undefined,
                  title: `POS Receipt ${offlineResult.localInvoiceNo}`
                });
              }
            }
            clearAll();
            return;
          } catch (offlineErr) {
            console.error('Offline save failed', offlineErr);
          }
        }
        throw apiErr;
      }

      setPaymentDialogOpen(false);
      setLastCreatedOrder(result);
      refreshNextOrderNumber();

      if (showPrintSchemeSelection) {
        setPrintSchemeDialogOpen(true);
      } else if (directPrint) {
        handleDirectPrint(result);
      } else if (previewPrint && enableWhatsappShare) {
        setPrintChoiceDialogOpen(true);
      } else if (previewPrint) {
        setPreviewPrintDialogOpen(true);
      } else if (enableWhatsappShare) {
        setWhatsappDialogOpen(true);
      } else {
        const wantsToPrint = window.confirm('Do you want to print the receipt?');
        if (wantsToPrint) {
          handleDirectPrint(result);
        } else {
          clearAll();
        }
      }
    } catch (e) {
      console.error('Save sale failed', e);
      let msg = e?.response?.data?.message || e?.message || 'Failed to save sale';
      const lower = String(msg || '').toLowerCase();
      if (lower.includes('day is not opened') || lower.includes('day not opened') || lower.includes('already closed')) {
        msg = 'Day is not open. Please open the day before saving the bill.';
      }
      alert(msg);
    }
  }, [cart, calculateTotals, paymentMethod, customer, notes, discount, receivedAmount, selectedSalesmanId, redeemedPoints, enqueueSnackbar, clearAll, enableWhatsappShare, enableBatchWise, directPrint, previewPrint, isQuotationMode, quotationId, editOrderId, nonGstMode, lineUnitPrice, refreshNextOrderNumber]);

  // Handle print with selected scheme
  const handleSchemePrint = async (order, format) => {
    if (!order?.id) {
      clearAll();
      return;
    }

    try {
      // Generate receipt with specific format
      if (window.electronAPI?.printEscPos) {
        try {
          const bytes = await salesService.generateReceiptEscPos(order.id, 'CUSTOMER', null, format);
          const defaultPrinter = await window.electronAPI.getDefaultPrinter();
          if (defaultPrinter) {
            await window.electronAPI.printEscPos(Array.from(bytes), {
              deviceName: defaultPrinter,
              title: `POS Receipt ${order.orderNumber || order.id}`
            });
            setPrintSchemeDialogOpen(false);
            clearAll();
            return;
          }
        } catch (escErr) {
          console.error('Electron ESC/POS print failed, falling back to PDF:', escErr);
        }
      }

      const receiptBlob = await salesService.generateReceipt(order.id, 'CUSTOMER', null, format);

      if (window.electronAPI) {
        // --- Electron Silent Print Path (PDF) ---
        try {
          const arrayBuffer = await receiptBlob.arrayBuffer();
          const data = Array.from(new Uint8Array(arrayBuffer));
          const defaultPrinter = await window.electronAPI.getDefaultPrinter();

          await window.electronAPI.printPdf(data, {
            deviceName: defaultPrinter || undefined,
            title: `POS Receipt ${order.orderNumber || order.id}`
          });

          setPrintSchemeDialogOpen(false);
          clearAll();
          return;
        } catch (electronErr) {
          console.error('Electron silent print failed, falling back to window.print():', electronErr);
        }
      }

      // --- Web/Browser Normal Path ---
      const blob = new Blob([receiptBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      setPrintSchemeDialogOpen(false);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        clearAll();
      }, 1000);
    } catch (error) {
      console.error('Error printing receipt with scheme:', error);
      enqueueSnackbar('Failed to print receipt', { variant: 'error' });
      setPrintSchemeDialogOpen(false);
      clearAll();
    }
  };

  // Handle direct print - opens print dialog immediately
  const handleDirectPrint = async (order) => {
    if (!order?.id) {
      clearAll();
      return;
    }

    try {
      // Electron: prefer ESC/POS direct to thermal (no PDF); fallback to existing PDF path
      if (window.electronAPI?.printEscPos) {
        try {
          const bytes = await salesService.generateReceiptEscPos(order.id, 'CUSTOMER', null, null);
          const defaultPrinter = await window.electronAPI.getDefaultPrinter();
          if (defaultPrinter) {
            await window.electronAPI.printEscPos(Array.from(bytes), {
              deviceName: defaultPrinter,
              title: `POS Receipt ${order.orderNumber || order.id}`
            });
            clearAll();
            return;
          }
        } catch (escErr) {
          console.error('Electron ESC/POS print failed, falling back to PDF:', escErr);
        }
      }

      // Generate receipt (PDF) — web, Android, or Electron fallback
      const receiptBlob = await salesService.generateReceipt(order.id, 'CUSTOMER', null);

      if (window.electronAPI) {
        // --- Electron Silent Print Path (PDF) ---
        try {
          const arrayBuffer = await receiptBlob.arrayBuffer();
          const data = Array.from(new Uint8Array(arrayBuffer));
          const defaultPrinter = await window.electronAPI.getDefaultPrinter();

          await window.electronAPI.printPdf(data, {
            deviceName: defaultPrinter || undefined,
            title: `POS Receipt ${order.orderNumber || order.id}`
          });

          clearAll();
          return; // Success, skip browser print
        } catch (electronErr) {
          console.error('Electron silent print failed, falling back to window.print():', electronErr);
          // Fallback to normal behavior below
        }
      }

      // --- Web/Browser Normal Path ---
      const blob = new Blob([receiptBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      // Clean up after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        clearAll();
      }, 1000);
    } catch (error) {
      console.error('Error printing receipt:', error);
      enqueueSnackbar('Failed to print receipt', { variant: 'error' });
      clearAll();
    }
  };

  // Load PDF preview when preview dialog opens
  useEffect(() => {
    if (previewPrintDialogOpen && lastCreatedOrder?.id) {
      loadPreviewPdf();
    }

    return () => {
      // Cleanup PDF URL when dialog closes
      if (previewPdfUrl) {
        window.URL.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPrintDialogOpen, lastCreatedOrder?.id]);

  const loadPreviewPdf = async () => {
    if (!lastCreatedOrder?.id) return;

    try {
      setLoadingPreview(true);
      // Generate receipt PDF
      const receiptBlob = await salesService.generateReceipt(lastCreatedOrder.id, 'CUSTOMER', null);
      const blob = new Blob([receiptBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (error) {
      console.error('Error loading preview PDF:', error);
      enqueueSnackbar('Failed to load bill preview', { variant: 'error' });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle preview print - prints the previewed PDF
  const handlePreviewPrint = async () => {
    if (!previewPdfUrl) {
      enqueueSnackbar('Preview not loaded', { variant: 'error' });
      return;
    }

    try {
      // Open in new window for printing
      const printWindow = window.open(previewPdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      setPreviewPrintDialogOpen(false);
      setTimeout(() => {
        if (previewPdfUrl) {
          window.URL.revokeObjectURL(previewPdfUrl);
          setPreviewPdfUrl(null);
        }
        clearAll();
      }, 1000);
    } catch (error) {
      console.error('Error printing receipt:', error);
      enqueueSnackbar('Failed to print receipt', { variant: 'error' });
    }
  };

  // Handle preview dialog close
  const handlePreviewDialogClose = () => {
    setPreviewPrintDialogOpen(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    clearAll();
  };

  // Handle WhatsApp share confirmation
  const handleWhatsappShareConfirm = async () => {
    if (!lastCreatedOrder?.id) {
      setWhatsappDialogOpen(false);
      clearAll();
      return;
    }

    try {
      await shareBillToWhatsApp(
        lastCreatedOrder.id,
        customer,
        lastCreatedOrder.orderNumber || lastCreatedOrder.id
      );
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      alert('Failed to share bill to WhatsApp');
    } finally {
      setWhatsappDialogOpen(false);
      clearAll();
    }
  };

  // Handle WhatsApp share cancel
  const handleWhatsappShareCancel = () => {
    setWhatsappDialogOpen(false);
    clearAll();
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      // F1: Add Customer
      if (e.key === 'F1') {
        e.preventDefault();
        setCustomerDialogOpen(true);
        return;
      }

      // F2: Split Bill
      if (e.key === 'F2') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          setSplitBillDialogOpen(true);
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F3: Direct Card Bill (Replaces Name Search)
      if (e.key === 'F3') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          handlePayment('CREDIT_CARD');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F4: Direct UPI Bill
      if (e.key === 'F4') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          handlePayment('PAYNOW');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F5: Direct Credit Bill
      if (e.key === 'F5') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          handlePayment('CREDIT');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // Space: Direct Cash Bill (Only when not in an input)
      if (e.code === 'Space') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable ||
          activeElement.getAttribute('role') === 'textbox' ||
          activeElement.getAttribute('role') === 'combobox';

        if (!isInput) {
          e.preventDefault();
          const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
          if (validItems.length > 0) {
            handlePayment('CASH');
          } else {
            enqueueSnackbar('Add items first', { variant: 'warning' });
          }
          return;
        }
      }

      // F6: Tender/Payment
      if (e.key === 'F6') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          setPaymentDialogOpen(true);
        }
        return;
      }

      // F7: Clear Screen
      if (e.key === 'F7') {
        e.preventDefault();
        clearAll();
        return;
      }

      // F11: Select Customer
      if (e.key === 'F11') {
        e.preventDefault();
        setCustomerDialogOpen(true);
        return;
      }

      // Delete key: Remove row
      if (e.key === 'Delete') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;

        if (!isInput && selectedRowIndex !== null && cart[selectedRowIndex]) {
          setRowToDelete(selectedRowIndex);
          setDeleteConfirmOpen(true);
        }
      }

      // P key: Show purchase price of selected row's product
      if (e.key === 'p' || e.key === 'P') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;

        if (!isInput && selectedRowIndex !== null && cart[selectedRowIndex]?.product) {
          e.preventDefault();
          const item = cart[selectedRowIndex];
          const purchasePrice = item.product?.purchasePrice ?? item.product?.costPrice ?? 0;
          setPurchasePriceDialogData({
            productName: item.product?.name || item.productName || 'Product',
            purchasePrice: Number(purchasePrice) || 0
          });
          setPurchasePriceDialogOpen(true);
        }
      }

      // Ctrl+S: Quick save (respects selected payment method; Split Bill opens its dialog)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
        if (validItems.length > 0) {
          if (paymentMethod === 'SPLIT_BILL') {
            setSplitBillDialogOpen(true);
          } else {
            handlePayment();
          }
        }
      }

      // Ctrl+V: Open Sales History
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        setSalesHistoryDialogOpen(true);
      }

      // Ctrl+Q: Toggle Non-GST mode (tax-included rate in Sale Rate, GST shows 0)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'q') {
        e.preventDefault();
        setNonGstMode((prev) => {
          const next = !prev;
          setCart((c) =>
            c.map((it) => {
              if (!it.product) return it;
              const taxRate = (it.product?.tax?.rate || 0) / 100;
              if (next) {
                const basePrice = lineUnitPrice(it);
                const taxInclusive = basePrice * (1 + taxRate);
                return { ...it, price: Math.round(taxInclusive * 100) / 100 };
              } else {
                const inclusivePrice = lineUnitPrice(it);
                const basePrice = taxRate > 0 ? inclusivePrice / (1 + taxRate) : inclusivePrice;
                return { ...it, price: Math.round(basePrice * 100) / 100 };
              }
            })
          );
          return next;
        });
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cart, handlePayment, clearAll, selectedRowIndex, deleteRow, deleteConfirmOpen, paymentMethod, nonGstMode, lineUnitPrice]);

  // Load quotation or existing order when navigating from View Orders
  useEffect(() => {
    const loadQuotation = async () => {
      try {
        const quotationDataStr = localStorage.getItem('quotationToLoad');
        if (!quotationDataStr) return;

        const quotationData = JSON.parse(quotationDataStr);

        // Clear the quotation data from localStorage
        localStorage.removeItem('quotationToLoad');

        // If quotation doesn't have items, fetch them
        let quotation = quotationData;
        if (!quotation.items || quotation.items.length === 0) {
          quotation = await salesService.getSalesOrderById(quotation.id);
        }

        // Set customer
        if (quotation.customer) {
          setCustomer(quotation.customer);
        }

        // Load items into cart
        if (quotation.items && quotation.items.length > 0) {
          const cartItems = quotation.items.map((item, index) => {
            // Build product object with tax information
            const product = {
              ...(item.product || {}),
              id: item.productId || item.product?.id,
              name: item.productName || item.product?.name || 'Unknown Product',
              salePrice: item.unitPrice,
              sellingPrice: item.unitPrice,
              tax: item.tax || item.product?.tax || null
            };

            return {
              id: index + 1,
              itemCode: product.sku || product.barcode || '',
              productName: product.name,
              quantity: normalizeLoadedLineQuantity(item.quantity),
              price: item.unitPrice,
              discount: item.discount || 0,
              discountPercentage: item.discountPercentage || 0,
              discountAmount: item.discountAmount || item.discount || 0,
              product: product,
              selectedUnit: item.unit || null,
              productBatchId: enableBatchWise ? (item.productBatchId || null) : null,
              batchNumber: enableBatchWise ? (item.batchNumber || null) : null,
              expiryDate: enableBatchWise ? (item.expiryDate || null) : null,
              mfd: enableBatchWise ? (item.mfd || null) : null
            };
          });

          // Add one empty row at the end
          cartItems.push({
            id: cartItems.length + 1,
            itemCode: '',
            productName: '',
            quantity: 1,
            price: 0,
            discount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            product: null,
            selectedUnit: null,
            productBatchId: null,
            batchNumber: null,
            expiryDate: null,
            mfd: null,
            selectedBatch: null
          });

          setCart(cartItems);

          // Set discount if present
          if (quotation.discount) {
            setDiscount(Number(quotation.discount));
          }
        }

        // Store quotation ID for reference (will be used when saving)
        setQuotationId(quotation.id);

        // Set quotation mode to false (we're converting to bill)
        setIsQuotationMode(false);

        // Restore Non-GST mode if quotation was non-GST
        setNonGstMode(!!quotation.nonGst);

        enqueueSnackbar('Quotation loaded. You can edit items and proceed to bill.', {
          variant: 'info',
          autoHideDuration: 4000
        });
      } catch (error) {
        console.error('Error loading quotation:', error);
        enqueueSnackbar('Failed to load quotation data', { variant: 'error' });
      }
    };

    const loadOrderForEdit = async () => {
      try {
        const orderIdStr = localStorage.getItem('orderToEditId');
        if (!orderIdStr) return;

        const orderId = Number(orderIdStr);
        if (!orderId) return;

        // Clear the value immediately to avoid re-loading on refresh
        localStorage.removeItem('orderToEditId');

        const order = await salesService.getSalesOrderById(orderId);

        // Set customer
        if (order.customer) {
          setCustomer(order.customer);
        }

        // Load items into cart
        if (order.items && order.items.length > 0) {
          const loadedDisc = Number(order.discount) || 0;
          const cartItems = order.items.map((item, index) => {
            // Build product object with tax information
            const product = {
              ...(item.product || {}),
              id: item.productId || item.product?.id,
              name: item.productName || item.product?.name || 'Unknown Product',
              salePrice: item.unitPrice,
              sellingPrice: item.unitPrice,
              tax: item.tax || item.product?.tax || null
            };

            return {
              id: index + 1,
              itemCode: product.sku || product.barcode || '',
              productName: product.name,
              quantity: normalizeLoadedLineQuantity(item.quantity),
              price: item.unitPrice,
              discount: item.discount || 0,
              discountPercentage: item.discountPercentage || 0,
              discountAmount: item.discountAmount || item.discount || 0,
              product: product,
              selectedUnit: item.unit || null,
              productBatchId: enableBatchWise ? (item.productBatchId || null) : null,
              batchNumber: enableBatchWise ? (item.batchNumber || null) : null,
              expiryDate: enableBatchWise ? (item.expiryDate || null) : null,
              mfd: enableBatchWise ? (item.mfd || null) : null
            };
          });

          // Add one empty row at the end
          cartItems.push({
            id: cartItems.length + 1,
            itemCode: '',
            productName: '',
            quantity: 1,
            price: 0,
            discount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            product: null,
            selectedUnit: null,
            productBatchId: null,
            batchNumber: null,
            expiryDate: null,
            mfd: null,
            selectedBatch: null
          });

          const derived = deriveOverallDiscountStateForLoadedOrder(loadedDisc, cartItems, !!order.nonGst);
          setDiscountType(derived.discountType);
          setOverallDiscountPercentage(derived.overallDiscountPercentage);
          setDiscount(loadedDisc);
          setCart(cartItems);
        } else if (order.discount) {
          setDiscount(Number(order.discount));
        }

        // Store order ID for reference when saving
        setEditOrderId(order.id);

        // Ensure quotation mode is off while editing a normal bill
        setIsQuotationMode(false);

        // Restore Non-GST mode if order was non-GST
        setNonGstMode(!!order.nonGst);

        enqueueSnackbar('Order loaded for editing. You can adjust items and save.', {
          variant: 'info',
          autoHideDuration: 4000
        });
      } catch (error) {
        console.error('Error loading order for edit:', error);
        enqueueSnackbar('Failed to load order for editing', { variant: 'error' });
      }
    };

    loadQuotation();
    loadOrderForEdit();
  }, [enqueueSnackbar]); // Run only once on mount

  // Initial load
  useEffect(() => {
    loadAllProducts();
    loadCustomers();
    loadUnits();
    loadRecentOrders();
    // Removed loadLocations() - location field removed for admin users
  }, [loadAllProducts, loadCustomers, loadUnits, loadRecentOrders]);

  // Apply initial focus dynamically based on POS Settings
  useEffect(() => {
    if (initialFocusAppliedRef.current) return;
    const t = setTimeout(() => {
      applyConfiguredInitialFocus();
      initialFocusAppliedRef.current = true;
    }, 350);
    return () => clearTimeout(t);
  }, [applyConfiguredInitialFocus]);

  // When user returns to this tab/window, re-focus according to settings (esp. CUSTOMER mode)
  useEffect(() => {
    const maybeRefocus = () => {
      if (document.visibilityState !== 'visible') return;
      // Don't steal focus if a dialog is open (payment/print/etc.)
      if (paymentDialogOpen || previewPrintDialogOpen || printChoiceDialogOpen || whatsappDialogOpen || customerDialogOpen || addCustomerDialogOpen) {
        return;
      }
      // Only re-apply automatically for CUSTOMER mode (requested behavior)
      if ((initialFocus || 'GRID').toUpperCase() !== 'CUSTOMER') return;
      setTimeout(() => applyConfiguredInitialFocus({ force: true }), 100);
    };

    window.addEventListener('focus', maybeRefocus);
    document.addEventListener('visibilitychange', maybeRefocus);
    return () => {
      window.removeEventListener('focus', maybeRefocus);
      document.removeEventListener('visibilitychange', maybeRefocus);
    };
  }, [initialFocus, paymentDialogOpen, previewPrintDialogOpen, printChoiceDialogOpen, whatsappDialogOpen, customerDialogOpen, addCustomerDialogOpen, applyConfiguredInitialFocus]);

  // Calculate totals
  const totals = calculateTotals();
  const itemCount = cart.filter(item => item.product).length;
  // const totalQty = cart.reduce((sum, item) => item.product ? sum + item.quantity : sum, 0); // Unused variable

  // Non-GST mode: professional Teal/Green scheme vs normal (blue)
  const headerBg = nonGstMode ? '#006064' : '#154c79'; // Cyan 900
  const tableHeaderBg = nonGstMode ? '#00796b' : '#154c79'; // Teal 700
  const pageBg = nonGstMode ? '#f1f8e9' : '#f1f1f1'; // Light Green 50
  const chipItemsBg = nonGstMode ? '#b2dfdb' : '#e0f2f1'; // Teal 100
  const chipItemsColor = nonGstMode ? '#004d40' : '#00695c'; // Teal 900
  const chipQtyBg = nonGstMode ? '#80cbc4' : '#e3f2fd'; // Teal 200
  const chipQtyColor = nonGstMode ? '#00695c' : '#1565c0'; // Teal 800

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: pageBg,
      fontFamily: '"Roboto", sans-serif',
      transition: 'background-color 0.3s ease'
    }}>
      {/* 1. Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: headerBg,
          color: 'white',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
            POS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={user.logoUrl ? `${user.logoUrl}${user.logoUrl.includes('?') ? '&' : '?'}_t=${Date.now()}` : ''}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'white',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              {!user.logoUrl && <BusinessIcon color="primary" sx={{ fontSize: 18 }} />}
            </Avatar>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 'bold' }}
            >
              {user.companyName || 'Tiya Inventory'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* Quotation Mode Toggle in Top Bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: isQuotationMode ? 'rgba(0, 150, 136, 0.1)' : 'transparent',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: isQuotationMode ? '1px solid #009688' : 'none'
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isQuotationMode}
                  onChange={(e) => setIsQuotationMode(e.target.checked)}
                  color="success"
                  size="small"
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: isQuotationMode ? '#009688' : '#ccc'
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DescriptionIcon fontSize="small" sx={{ color: isQuotationMode ? '#009688' : 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: isQuotationMode ? 'bold' : 'normal' }}>
                    Quotation Bill
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {/* Hold Bills Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setViewHoldBillsModalOpen(true)}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                height: 28,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'white' }
              }}
              startIcon={<ListIcon sx={{ fontSize: '1.2rem !important' }} />}
            >
              View Hold Bills
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setHoldBillModalOpen(true)}
              disabled={cart.length === 0 || !cart[0].product}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                height: 28,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'white' },
                '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.3)' }
              }}
              startIcon={<SaveIcon sx={{ fontSize: '1.2rem !important' }} />}
            >
              Hold Bill
            </Button>
          </Box>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {nextOrderNumber ? (
              <>
                Bill No: <span style={{ fontWeight: 'normal' }}>{nextOrderNumber}</span>
                <span style={{ marginLeft: '16px' }} />
              </>
            ) : null}
            Bill Date: <span style={{ fontWeight: 'normal' }}>{new Date().toLocaleDateString('en-GB')}</span>
            <span style={{ fontWeight: 'normal', marginLeft: '12px' }}>{currentTime.toLocaleTimeString('en-US', { hour12: true })}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/sales/view-orders')}
              sx={{ backgroundColor: 'white', color: headerBg, textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#f5f5f5' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>←</span>}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={clearAll}
              sx={{ borderColor: '#ef5350', color: '#ef5350', backgroundColor: 'white', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef5350' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>⟲</span>}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 2. Top Info Section - flex layout so controls fill space evenly */}
      <Paper elevation={1} sx={{ p: 1.5, m: 1, borderRadius: 1, backgroundColor: nonGstMode ? '#fff3e0' : 'white' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, width: '100%' }}>
          {/* Customer - equal flex */}
          <Box sx={{ flex: '1 1 160px', minWidth: 160, maxWidth: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Customer</Typography>
              {customer && customer.id !== 1 && (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                    Points: {customer.loyaltyPoints ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'red', fontWeight: 'bold' }}>
                    Due Balance: {getCustomerCreditBalance(customer).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Autocomplete
              fullWidth
              size="small"
              options={[...customers, { id: 'ADD_NEW', name: '+ Add New Customer', phone: '' }]}
              getOptionLabel={(option) => option.name || ''}
              value={customer}
              autoHighlight
              onChange={(e, newValue) => {
                if (newValue && newValue.id === 'ADD_NEW') {
                  setAddCustomerDialogOpen(true);
                  return;
                }
                // Set selected customer
                setCustomer(newValue);
              }}
              filterOptions={(options, params) => {
                const addNew = options.find((o) => o && o.id === 'ADD_NEW');
                const filtered = options.filter((option) => {
                  if (option && option.id === 'ADD_NEW') return false; // keep for last
                  const searchText = params.inputValue.toLowerCase();
                  const addr = option.address != null ? String(option.address).toLowerCase() : '';
                  return (
                    (option.name && option.name.toLowerCase().includes(searchText)) ||
                    (option.phone && option.phone.toLowerCase().includes(searchText)) ||
                    (addr && addr.includes(searchText))
                  );
                });
                if (addNew) filtered.push(addNew);
                return filtered;
              }}
              onHighlightChange={(e, option) => setCustomerListHighlight(option)}
              onClose={() => setCustomerListHighlight(null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Customer..."
                  sx={{ '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: 'white' } }}
                  inputRef={(el) => {
                    if (el) customerInputRef.current = el;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      // Let Autocomplete select highlighted option first (keyboard-friendly)
                      if (params.inputProps && params.inputProps.onKeyDown) {
                        params.inputProps.onKeyDown(e);
                      }
                      if (e.defaultMuiPrevented) return;
                      e.preventDefault();
                      // Move focus to first item code after pressing Enter on customer field
                      if (itemCodeRefs.current[0]) {
                        itemCodeRefs.current[0].focus();
                        if (itemCodeRefs.current[0].select) {
                          itemCodeRefs.current[0].select();
                        }
                      }
                    }
                    // Allow Autocomplete's own handlers to still run
                    if (params.inputProps && params.inputProps.onKeyDown) {
                      params.inputProps.onKeyDown(e);
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const addr = getCustomerAddressDisplay(option);
                const tip =
                  option.id === 'ADD_NEW'
                    ? 'Create a new customer'
                    : (addr || 'No address on file');
                const keyboardHighlighted =
                  customerListHighlight != null &&
                  option != null &&
                  customerListHighlight.id === option.id;
                return (
                  <li
                    {...props}
                    style={{
                      color: option.id === 'ADD_NEW' ? '#1565c0' : 'inherit',
                      fontWeight: option.id === 'ADD_NEW' ? 'bold' : 'normal',
                      borderBottom: option.id === 'ADD_NEW' ? '1px dashed #ccc' : 'none'
                    }}
                  >
                    <PosCustomerOptionTooltip keyboardHighlighted={keyboardHighlighted} title={tip}>
                      <Box sx={{ width: '100%', py: 0.25 }} component="div">
                        {option.name} {option.phone ? `(${option.phone})` : ''}
                      </Box>
                    </PosCustomerOptionTooltip>
                  </li>
                );
              }}
            />
            {customerListHighlight && customerListHighlight.id !== 'ADD_NEW' && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  mt: 0.5,
                  color: 'text.secondary',
                  lineHeight: 1.35,
                  maxWidth: '100%',
                  wordBreak: 'break-word'
                }}
              >
                {getCustomerAddressDisplay(customerListHighlight)
                  ? `Address: ${getCustomerAddressDisplay(customerListHighlight)}`
                  : 'No address on file'}
              </Typography>
            )}
          </Box>

          {/* Salesman - equal flex */}
          <Box sx={{ flex: '1 1 140px', minWidth: 140, maxWidth: 260 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Salesman</Typography>
            <Select
              fullWidth
              size="small"
              value={selectedSalesmanId || ''}
              onChange={(e) => setSelectedSalesmanId(e.target.value || '')}
              displayEmpty
              sx={{ backgroundColor: 'white', fontSize: '14px', height: '40px' }}
              renderValue={(v) => {
                if (!v) return 'None';
                const sm = salesmen.find(s => s.id === parseInt(v, 10));
                return sm ? `${sm.name}${sm.phone ? ` (${sm.phone})` : ''}` : 'None';
              }}
            >
              <MenuItem value="">None</MenuItem>
              {salesmen.map((sm) => (
                <MenuItem key={sm.id} value={sm.id}>{sm.name}{sm.phone ? ` (${sm.phone})` : ''}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Payment Mode - equal flex */}
          <Box sx={{ flex: '1 1 120px', minWidth: 120, maxWidth: 200 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Payment Mode</Typography>
            <Select
              fullWidth
              size="small"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ backgroundColor: 'white', fontSize: '14px', height: '40px' }}
            >
              <MenuItem value="CASH">💵 Cash</MenuItem>
              <MenuItem value="CREDIT_CARD">💳 Card</MenuItem>
              <MenuItem value="PAYNOW">📱 UPI</MenuItem>
              <MenuItem value="CREDIT">📝 Credit</MenuItem>
              <MenuItem value="SPLIT_BILL">💰 Split Bill</MenuItem>
            </Select>
          </Box>

          {/* Items / Qty Stats and Price Mode (Retailer / Wholesale) */}
          <Box sx={{ flex: '1 1 140px', minWidth: 140, maxWidth: 220, display: 'flex', gap: 0.5, pb: 0.5 }}>
            <Chip
              label={`Items: ${cart.filter(c => c.product).length}`}
              sx={{ backgroundColor: chipItemsBg, color: chipItemsColor, fontWeight: 'bold', border: nonGstMode ? '1px solid #ffcc80' : '1px solid #b2dfdb', height: '40px', fontSize: '13px', flex: 1, minWidth: 0 }}
            />
            <Chip
              label={`Qty: ${formatTotalQtyBadge(cart, pageFieldConfig.allowDecimalQty)}`}
              sx={{ backgroundColor: chipQtyBg, color: chipQtyColor, fontWeight: 'bold', border: nonGstMode ? '1px solid #ffb74d' : '1px solid #bbdefb', height: '40px', fontSize: '13px', flex: 1, minWidth: 0 }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 130, pb: 0.5 }}>
            <InputLabel id="pos-price-mode-label">Rate</InputLabel>
            <Select
              labelId="pos-price-mode-label"
              value={priceMode}
              label="Rate"
              onChange={(e) => setPriceMode(e.target.value)}
              sx={{
                height: '40px',
                backgroundColor: priceMode === 'wholesale' ? '#fff3e0' : '#e8f5e9',
                fontWeight: 'bold',
                fontSize: '13px'
              }}
            >
              <MenuItem value="retailer">Retail</MenuItem>
              <MenuItem value="wholesale">Wholesale</MenuItem>
            </Select>
          </FormControl>

          {/* Total - always at the right corner (marginLeft: auto pushes it right) */}
          <Box sx={{ flex: '0 0 auto', marginLeft: 'auto', alignSelf: 'center', textAlign: 'right', minWidth: 140 }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#e74c3c', lineHeight: 1, fontSize: '3rem' }}>
              {totals.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* 3. Product Grid */}
      <Box sx={{ flexGrow: 1, px: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer component={Paper} elevation={1} sx={{ height: '100%', borderRadius: 1, backgroundColor: nonGstMode ? '#fffde7' : 'white' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '4%' }}>S.No</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '15%', textAlign: 'left' }}>Item Code</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '30%', textAlign: 'left' }}>Product Name</StyledTableHeadCell>
                {enableBatchWise && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '14%', textAlign: 'left' }}>Batch</StyledTableHeadCell>}
                {showMrp && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '9%', textAlign: 'right' }}>MRP</StyledTableHeadCell>}
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '8%', textAlign: 'center' }}>Qty</StyledTableHeadCell>
                {showUom && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '10%', textAlign: 'center' }}>UOM</StyledTableHeadCell>}
                {showPurchaseRate && (
                  <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '10%', textAlign: 'right' }}>Purchase Rate</StyledTableHeadCell>
                )}
                {pageFieldConfig.showSaleRate !== false && (
                  <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '12%', textAlign: 'right' }}>Sale Rate</StyledTableHeadCell>
                )}
                {pageFieldConfig.showSaleRateInclusive && (
                  <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '12%', textAlign: 'right' }}>Sale Rate</StyledTableHeadCell>
                )}
                {showGst && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '8%', textAlign: 'center' }}>GST(%)</StyledTableHeadCell>}
                {showDiscount && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '15%', textAlign: 'center' }}>Discount (% / ₹)</StyledTableHeadCell>}
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '12%', textAlign: 'right' }}>Amount</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.map((item, index) => {
                const isSearching = !item.product;
                const rowBg = isSearching ? '#fffde7' : (nonGstMode ? (index % 2 === 0 ? '#fff8e1' : '#ffecb3') : (index % 2 === 0 ? '#f8f9fb' : 'white'));

                return (
                  <TableRow
                    key={item.id}
                    onClick={() => {
                      setSelectedRowIndex(index);
                      if (item.product && pageFieldConfig.showItemHistory) {
                        setProductHistoryTab('productHistory');
                        loadProductHistory(item.product, customer);
                      }
                    }}
                    sx={{
                      backgroundColor: selectedRowIndex === index ? '#fff9c4' : rowBg,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: selectedRowIndex === index ? '#fff9c4' : '#f5f5f5' }
                    }}
                  >
                    <StyledTableCell align="center">{index + 1}</StyledTableCell>

                    {/* Item Code (fixed column - always shown) */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <Autocomplete
                        freeSolo
                        options={productOptions[index] || []}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.sku || option.name || '';
                        }}
                        filterOptions={(x) => x}
                        value={item.product || null}
                        inputValue={item.itemCode || ''}
                        onInputChange={(e, value, reason) => {
                          if (reason === 'input') handleItemCodeInputChange(index, value);
                          else if (reason === 'clear') {
                            handleItemCodeInputChange(index, '');
                            clearRowProduct(index);
                          }
                        }}
                        onChange={(e, value) => {
                          if (value && typeof value === 'object') handleProductSelect(index, value);
                          else if (value === null) clearRowProduct(index);
                        }}
                        autoHighlight
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={isSearching ? "scan or type..." : ""}
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                            inputRef={(el) => { if (el) itemCodeRefs.current[index] = el; }}
                            onKeyDown={(e) => {
                              const options = productOptions[index] || [];
                              const hasOptions = options.length > 0;
                              if (e.key === 'Enter') {
                                // When dropdown has options, let Autocomplete handle Enter so it selects the *highlighted* option (matches mouse click).
                                if (hasOptions) {
                                  params.onKeyDown?.(e);
                                  return;
                                }
                                e.preventDefault();
                                const code = (item.itemCode || '').trim();
                                if (!code) {
                                  // Empty item code should not create/move to a new row.
                                  return;
                                }

                                // 1) If this code already exists in the cart (by SKU/barcode/code),
                                // reuse that product so quantity is increased instead of typing text.
                                const lowerCode = code.toLowerCase();
                                const existing = cart.find((c) =>
                                  c.product &&
                                  (
                                    (c.product.sku && c.product.sku.toLowerCase() === lowerCode) ||
                                    (c.product.productCode && c.product.productCode.toLowerCase() === lowerCode) ||
                                    (c.product.product_code && c.product.product_code.toLowerCase() === lowerCode) ||
                                    (c.product.barcode && c.product.barcode.toLowerCase() === lowerCode) ||
                                    (c.product.code && c.product.code.toLowerCase() === lowerCode)
                                  )
                                );

                                if (existing && existing.product) {
                                  // Use current row as the scanning row; handleProductSelect will
                                  // bump quantity on the existing row and clear this row.
                                  handleProductSelect(index, existing.product);
                                  return;
                                }

                                // 2) Otherwise, run the normal product search by code/barcode.
                                searchProduct(code, index);
                                return;
                              }
                              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                if (hasOptions) {
                                  params.onKeyDown?.(e);
                                  return;
                                }
                                handleGridArrowNav(e, index, 'itemCode');
                                return;
                              }
                              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                                const navOut =
                                  e.key === 'ArrowLeft'
                                    ? posInputAllowsNavOutLeft(e.target)
                                    : posInputAllowsNavOutRight(e.target);
                                if (navOut) {
                                  handleGridArrowNav(e, index, 'itemCode');
                                  return;
                                }
                                params.onKeyDown?.(e);
                                return;
                              }
                              params.onKeyDown?.(e);
                            }}
                            sx={{ '& input': { fontSize: '15px', padding: '4px' } }}
                          />
                        )}
                        renderOption={(props, option) => {
                          // Show Product Name big, Item Code (SKU) smaller
                          return (
                            <li {...props}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {option.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {option.sku || ''}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                      />
                    </StyledTableCell>

                    {/* Product Name (fixed column) */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                        {item.productName || '-'}
                      </Typography>
                    </StyledTableCell>

                    {enableBatchWise && (
                      /* Batch (FEFO default, cashier can change) */
                      <StyledTableCell sx={{ p: 0.5 }}>
                        {!item.product ? (
                          <Typography variant="body2" sx={{ padding: '4px', fontSize: '13px', color: 'text.secondary' }}>-</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <Select
                              value={item.productBatchId ?? ''}
                              onChange={(e) => handleBatchChange(index, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              size="small"
                              displayEmpty
                              variant="standard"
                              disableUnderline
                              sx={{ fontSize: '13px', minWidth: '120px', '& .MuiSelect-select': { py: 0.25 } }}
                            >
                              <MenuItem value="">
                                <em>No batch</em>
                              </MenuItem>
                              {sortBatchesByFefo(productBatchesByProduct[item.product.id] || []).map((b) => {
                                const expired = isBatchExpiredPos(b.expiryDate);
                                return (
                                  <MenuItem key={b.id} value={b.id} disabled={expired}>
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                      <span>
                                        {b.batchNumber} ({Number(b.quantity || 0)})
                                      </span>
                                      {expired ? (
                                        <Typography component="span" variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                                          Expired
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <Typography
                              variant="caption"
                              sx={{
                                color: item.expiryDate && isBatchExpiredPos(item.expiryDate) ? 'error.main' : 'text.secondary',
                                fontWeight: item.expiryDate && isBatchExpiredPos(item.expiryDate) ? 600 : 400,
                                lineHeight: 1.2
                              }}
                            >
                              {item.expiryDate
                                ? `Exp: ${formatExpiryForPos(item.expiryDate)}${isBatchExpiredPos(item.expiryDate) ? ' (expired)' : ''}`
                                : 'No expiry'}
                            </Typography>
                          </Box>
                        )}
                      </StyledTableCell>
                    )}

                    {/* MRP - optional column */}
                    {showMrp && (
                      <StyledTableCell align="right" sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                          {(item.selectedBatch?.mrp ?? item.product?.mrp) != null ? Number(item.selectedBatch?.mrp ?? item.product?.mrp).toFixed(2) : '-'}
                        </Typography>
                      </StyledTableCell>
                    )}

                    {/* Qty */}
                    {/* Qty */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <TextField
                        type="text"
                        inputMode={pageFieldConfig.allowDecimalQty ? 'decimal' : 'numeric'}
                        value={item.quantity === '' || item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleTabOrderEnter(index, 'qty');
                            return;
                          }
                          handleGridArrowNav(e, index, 'qty');
                        }}
                        inputRef={(el) => (qtyRefs.current[index] = el)}
                        disabled={!item.product}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          '& input': {
                            fontSize: '15px',
                            padding: '4px',
                            textAlign: 'center',
                            backgroundColor: item.product ? '#fff' : 'transparent',
                            border: item.product ? '1px solid #ddd' : 'none',
                            borderRadius: '4px',
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                      />
                    </StyledTableCell>

                    {/* UOM - optional column */}
                    {showUom && (
                      <StyledTableCell align="center" sx={{ p: 0.5 }}>
                        {(() => {
                          if (!item.product) return '-';
                          const availableUnits = getAvailableUnits(item.product);
                          if (availableUnits.length > 1) {
                            const currentUnitId = item.selectedUnit?.id || (availableUnits.find(u => u.type === 'MAIN')?.id) || '';
                            return (
                              <Select
                                value={currentUnitId}
                                onChange={(e) => handleUOMChange(index, e.target.value)}
                                open={openUomRowIndex === index}
                                onOpen={() => {
                                  uomSkipTabAdvanceRef.current = false;
                                  setOpenUomRowIndex(index);
                                }}
                                onClose={() => {
                                  setOpenUomRowIndex((prev) => (prev === index ? null : prev));
                                  const skip = uomSkipTabAdvanceRef.current;
                                  uomSkipTabAdvanceRef.current = false;
                                  if (!skip) {
                                    // Defer until menu unmounts so Sale Rate / next field can receive focus.
                                    setTimeout(() => handleTabOrderEnter(index, 'uom'), 50);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                MenuProps={{
                                  onClose: (event, reason) => {
                                    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
                                      uomSkipTabAdvanceRef.current = true;
                                    }
                                    setOpenUomRowIndex((prev) => (prev === index ? null : prev));
                                  },
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'ArrowLeft') {
                                    e.preventDefault();
                                    setOpenUomRowIndex(null);
                                    const keys = getGridNavFieldKeysForRow(index);
                                    const ui = keys.indexOf('uom');
                                    const pk = ui > 0 ? keys[ui - 1] : 'qty';
                                    setTimeout(() => focusRef(pk, index), 0);
                                    return;
                                  }
                                  if (e.key === 'ArrowRight') {
                                    e.preventDefault();
                                    setOpenUomRowIndex(null);
                                    const keys = getGridNavFieldKeysForRow(index);
                                    const ui = keys.indexOf('uom');
                                    const nk = ui >= 0 && ui < keys.length - 1 ? keys[ui + 1] : null;
                                    if (nk) setTimeout(() => focusRef(nk, index), 0);
                                  }
                                }}
                                inputRef={(el) => { if (el) uomRefs.current[index] = el; }}
                                variant="standard"
                                disableUnderline
                                sx={{ fontSize: '15px', textAlign: 'center', width: '100%', '& .MuiSelect-select': { py: 0, textAlign: 'center' } }}
                              >
                                {availableUnits.map((u) => (
                                  <MenuItem
                                    key={u.id}
                                    value={u.id}
                                    sx={{ fontSize: '15px' }}
                                    onFocus={() => handleUOMChange(index, u.id)}
                                    onMouseEnter={() => handleUOMChange(index, u.id)}
                                  >
                                    {u.abbreviation || u.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            );
                          }
                          return item.selectedUnit?.abbreviation || item.selectedUnit?.name || item.product?.unit?.abbreviation || item.product?.unit?.name || item.product?.uom || '-';
                        })()}
                      </StyledTableCell>
                    )}

                    {showPurchaseRate && (
                      <StyledTableCell align="right" sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                          {(() => {
                            if (!item.product) return '-';
                            const pr = item.selectedBatch?.purchasePrice ?? item.product?.purchasePrice ?? item.product?.costPrice;
                            return pr != null && pr !== '' ? Number(pr).toFixed(2) : '-';
                          })()}
                        </Typography>
                      </StyledTableCell>
                    )}

                    {/* Sale Rate (Exclusive) */}
                    {pageFieldConfig.showSaleRate !== false && (
                      <StyledTableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          inputMode="decimal"
                          value={item.price === '' || item.price === null || item.price === undefined ? '' : String(item.price)}
                          onChange={(e) => handlePriceChange(index, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleTabOrderEnter(index, 'saleRate');
                              return;
                            }
                            handleGridArrowNav(e, index, 'saleRate');
                          }}
                          inputRef={(el) => (priceRefs.current[index] = el)}
                          disabled={!item.product}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            '& input': {
                              fontSize: '15px',
                              padding: '4px',
                              textAlign: 'right',
                              backgroundColor: item.product ? '#fff' : 'transparent',
                              border: item.product ? '1px solid #ddd' : 'none',
                              borderRadius: '4px',
                              '-moz-appearance': 'textfield',
                              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                              }
                            }
                          }}
                        />
                      </StyledTableCell>
                    )}

                    {/* Sale Rate (Inclusive) */}
                    {pageFieldConfig.showSaleRateInclusive && (
                      <StyledTableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          inputMode="decimal"
                          value={(() => {
                            if (item.price_inclusive_input !== undefined) return item.price_inclusive_input;
                            if (!item.product || item.price === '' || item.price === null || item.price === undefined) return '';
                            if (nonGstMode) return String(item.price);
                            const taxRate = (item.selectedBatch?.tax?.rate ?? item.product?.tax?.rate ?? 0) / 100;
                            const inclusive = Number(item.price) * (1 + taxRate);
                            return parseFloat(inclusive.toFixed(2)).toString();
                          })()}
                          onChange={(e) => handleInclusivePriceChange(index, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleTabOrderEnter(index, 'saleRateInclusive');
                              return;
                            }
                            handleGridArrowNav(e, index, 'saleRateInclusive');
                          }}
                          inputRef={(el) => (inclusivePriceRefs.current[index] = el)}
                          disabled={!item.product}
                          variant="standard"
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            '& input': {
                              fontSize: '15px',
                              padding: '4px',
                              textAlign: 'right',
                              backgroundColor: item.product ? '#fff' : 'transparent',
                              border: item.product ? '1px solid #ddd' : 'none',
                              borderRadius: '4px',
                              '-moz-appearance': 'textfield',
                              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                              }
                            }
                          }}
                        />
                      </StyledTableCell>
                    )}

                    {/* GST(%) - optional column */}
                    {showGst && (
                      <StyledTableCell align="center" sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                          {nonGstMode ? '0.00' : ((item.selectedBatch?.tax?.rate ?? item.product?.tax?.rate) != null ? Number(item.selectedBatch?.tax?.rate ?? item.product?.tax?.rate).toFixed(2) : '0.00')}
                        </Typography>
                      </StyledTableCell>
                    )}

                    {/* Discount - optional column */}
                    {showDiscount && (
                      <StyledTableCell sx={{ p: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                          <TextField
                            size="small"
                            type="number"
                            variant="outlined"
                            value={item.discountPercentage === 0 ? '' : item.discountPercentage}
                            onChange={(e) => handleItemDiscountChange(index, e.target.value, 'percentage')}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleTabOrderEnter(index, 'discount');
                                return;
                              }
                              handleGridArrowNav(e, index, 'discountPct');
                            }}
                            inputRef={(el) => { discountPctRefs.current[index] = el; }}
                            disabled={!item.product}
                            InputProps={{
                              endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '13px' } }}>%</InputAdornment>
                            }}
                            sx={{
                              width: '75px',
                              backgroundColor: item.product ? 'white' : 'transparent',
                              '& .MuiInputBase-root': { fontSize: '14px', height: '32px' },
                              '& input': {
                                textAlign: 'right',
                                padding: '4px 8px',
                                '-moz-appearance': 'textfield',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                }
                              }
                            }}
                          />
                          <TextField
                            size="small"
                            type="number"
                            variant="outlined"
                            value={item.discountAmount === 0 ? '' : item.discountAmount}
                            onChange={(e) => handleItemDiscountChange(index, e.target.value, 'amount')}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleTabOrderEnter(index, 'discount');
                                return;
                              }
                              handleGridArrowNav(e, index, 'discount');
                            }}
                            inputRef={(el) => (discountRefs.current[index] = el)}
                            disabled={!item.product}
                            InputProps={{
                              startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '13px' } }}>₹</InputAdornment>
                            }}
                            sx={{
                              width: '85px',
                              backgroundColor: item.product ? 'white' : 'transparent',
                              '& .MuiInputBase-root': { fontSize: '14px', height: '32px' },
                              '& input': {
                                textAlign: 'right',
                                padding: '4px 8px',
                                '-moz-appearance': 'textfield',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  '-webkit-appearance': 'none',
                                  margin: 0,
                                }
                              }
                            }}
                          />
                        </Box>
                      </StyledTableCell>
                    )}

                    {/* Amount (after tax: same logic as calculateTotals so row amount matches Net Amount) */}
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {item.product
                        ? (() => {
                          const itemTotal = (lineUnitPrice(item) * lineItemQty(item)) - (Number(item.discount) || 0);
                          if (nonGstMode) return itemTotal.toFixed(2);
                          const taxRate = (item.selectedBatch?.tax?.rate ?? item.product?.tax?.rate ?? 0) / 100;
                          const amountWithTax = itemTotal + (itemTotal * taxRate);
                          return amountWithTax.toFixed(2);
                        })()
                        : '0.00'}
                    </StyledTableCell>
                  </TableRow>
                );
              })}

              {/* Add New Row Hint */}
              <TableRow>
                <TableCell colSpan={5 + (enableBatchWise ? 1 : 0) + (showMrp ? 1 : 0) + (showUom ? 1 : 0) + (showPurchaseRate ? 1 : 0) + (pageFieldConfig.showSaleRate !== false ? 1 : 0) + (pageFieldConfig.showSaleRateInclusive ? 1 : 0) + (showGst ? 1 : 0) + (showDiscount ? 1 : 0)} align="center" sx={{ border: 'none', py: 1 }}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Press Enter on the last row to add a new item</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 4. Footer Section */}
      <Paper elevation={2} sx={{ p: 1, m: 1, mt: 0, borderRadius: 1 }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>

          {/* Left: Recent Transactions & Product History */}
          <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
              <Button
                size="small"
                variant={productHistoryTab === 'recentBills' ? 'contained' : 'text'}
                sx={{ borderRadius: 0, flex: 1 }}
                onClick={() => setProductHistoryTab('recentBills')}
              >
                Recent Bills
              </Button>
              {pageFieldConfig.showItemHistory && (
                <Button
                  size="small"
                  variant={productHistoryTab === 'productHistory' ? 'contained' : 'text'}
                  sx={{ borderRadius: 0, flex: 1 }}
                  onClick={() => setProductHistoryTab('productHistory')}
                >
                  Item History
                </Button>
              )}
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: nonGstMode ? '1px solid #ffcc80' : '1px solid #eee', flexGrow: 1, overflow: 'auto', backgroundColor: nonGstMode ? '#fffde7' : 'white' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  {productHistoryTab === 'recentBills' ? (
                    <TableRow>
                      <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', pl: 1, backgroundColor: tableHeaderBg, color: 'white' }} align="left">Invoice No</StyledTableHeadCell>
                      <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', backgroundColor: tableHeaderBg, color: 'white' }} align="left">Customer</StyledTableHeadCell>
                      <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', pr: 1, backgroundColor: tableHeaderBg, color: 'white' }} align="right">Total</StyledTableHeadCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <StyledTableHeadCell sx={{ py: 0.5, fontSize: '12px', pl: 0.5, backgroundColor: tableHeaderBg, color: 'white', whiteSpace: 'nowrap' }} align="left">Invoice No</StyledTableHeadCell>
                      <StyledTableHeadCell sx={{ py: 0.5, fontSize: '12px', backgroundColor: tableHeaderBg, color: 'white', whiteSpace: 'nowrap' }} align="left">Product</StyledTableHeadCell>
                      <StyledTableHeadCell sx={{ py: 0.5, fontSize: '12px', backgroundColor: tableHeaderBg, color: 'white', whiteSpace: 'nowrap' }} align="left">Bill Date</StyledTableHeadCell>
                      <StyledTableHeadCell sx={{ py: 0.5, fontSize: '12px', pr: 0.5, backgroundColor: tableHeaderBg, color: 'white', whiteSpace: 'nowrap' }} align="right">Sold Price (Incl. GST)</StyledTableHeadCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {productHistoryTab === 'recentBills' ? (
                    recentOrders.length > 0 ? (
                      recentOrders.map((order) => (
                        <TableRow key={order.id} hover>
                          <StyledTableCell sx={{ py: 1.5, fontSize: '14px', pl: 1 }}>
                            {order.orderNumber}
                          </StyledTableCell>
                          <StyledTableCell sx={{ py: 1.5, fontSize: '14px' }}>
                            {order.customer?.name || 'Walk-in'}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{ py: 1.5, fontSize: '14px', fontWeight: 'bold', pr: 1 }}>
                            {formatCurrency(order.total)}
                          </StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#999', fontSize: '14px' }}>
                          No recent transactions
                        </TableCell>
                      </TableRow>
                    )
                  ) : pageFieldConfig.showItemHistory ? (
                    productHistoryLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#999', fontSize: '14px' }}>
                          Loading item history...
                        </TableCell>
                      </TableRow>
                    ) : productHistoryError ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'error.main', fontSize: '14px' }}>
                          {productHistoryError}
                        </TableCell>
                      </TableRow>
                    ) : productHistory && productHistory.length > 0 ? (
                      productHistory.map((row, idx) => (
                        <TableRow key={`${row.invoiceNo}-${idx}`} hover>
                          <StyledTableCell sx={{ py: 0.5, fontSize: '12px', pl: 0.5 }}>
                            {row.invoiceNo}
                          </StyledTableCell>
                          <StyledTableCell sx={{ py: 0.5, fontSize: '12px' }}>
                            {row.productName}
                          </StyledTableCell>
                          <StyledTableCell sx={{ py: 0.5, fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {formatHistoryOrderDate(row.orderDate) || '-'}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{ py: 0.5, fontSize: '12px', fontWeight: 'bold', pr: 0.5 }}>
                            {formatCurrency(row.price)}
                          </StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#999', fontSize: '14px' }}>
                          No history for this item
                        </TableCell>
                      </TableRow>
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#999', fontSize: '14px' }}>
                        Item history is disabled
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Right Half: Discount, Totals & Action Buttons */}
          {/* Right Half: Discount, Totals & Action Buttons */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
            {/* Top: Discount Fields and Totals Box - Expanding to fill height */}
            <Grid container spacing={1} sx={{ flexGrow: 1 }}>
              {/* Overall Discount Fields */}
              <Grid item xs={12} sm={3} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2, 
                  width: '100%', 
                  mt: 2.5, // Move down from top edge
                  px: 1,
                  alignItems: 'flex-start' // Align fields to the left
                }}>
                  <TextField
                    label="Disc %"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={overallDiscountPercentage === 0 ? '' : overallDiscountPercentage}
                    onChange={(e) => handleOverallDiscountChange(e.target.value, 'percentage')}
                    onFocus={(e) => e.target.select()}
                    InputProps={{
                      endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '11px' } }}>%</InputAdornment>,
                      style: { fontSize: '13px', padding: '0 4px' }
                    }}
                    InputLabelProps={{ shrink: true, style: { fontSize: '12px' } }}
                    sx={{ 
                      backgroundColor: 'white',
                      maxWidth: '120px', // Reduced width
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px'
                      }
                    }}
                  />
                  <TextField
                    label="Discount Amt"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={discount === 0 ? '' : discount}
                    onChange={(e) => handleOverallDiscountChange(e.target.value, 'amount')}
                    onFocus={(e) => e.target.select()}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '11px' } }}>₹</InputAdornment>,
                      style: { fontSize: '13px', padding: '0 4px' }
                    }}
                    InputLabelProps={{ shrink: true, style: { fontSize: '12px' } }}
                    sx={{ 
                      backgroundColor: 'white',
                      maxWidth: '120px', // Reduced width
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px'
                      }
                    }}
                  />
                </Box>
              </Grid>

              {/* Totals & Net Amount Box - Filling space */}
              <Grid item xs={12} sm={9} sx={{ display: 'flex' }}>
                <Box sx={{
                  p: 0.8,
                  px: 1.5,
                  backgroundColor: nonGstMode ? '#ffecb3' : '#f9f9f9',
                  borderRadius: 1,
                  border: nonGstMode ? '1px solid #ffcc80' : '1px solid #dee2e6',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  flexGrow: 1, // Fill vertical space
                  boxShadow: 'inset 0 0 10px rgba(0,0,0,0.02)'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>Subtotal:</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '12px' }}>{formatCurrency(totals.subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>Tax:</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '12px' }}>{formatCurrency(totals.taxAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>Discount:</Typography>
                    <Typography variant="body2" color="error" fontWeight="bold" sx={{ fontSize: '12px' }}>-{formatCurrency(totals.discount)}</Typography>
                  </Box>
                  {roundOffEnabled && totals.roundOff && Math.abs(totals.roundOff) >= 0.005 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>Round off:</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '12px' }}>
                        {totals.roundOff > 0 ? '+' : '-'}
                        {formatCurrency(Math.abs(totals.roundOff))}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '15px', color: '#37474f' }}>Net Amount:</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold" sx={{ fontSize: '18px' }}>{formatCurrency(totals.total)}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Bottom Row: Shortcut Buttons + Bill/Print - Full Width Left to Right */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.6,
              flexWrap: 'nowrap',
              backgroundColor: '#f1f3f5',
              p: 0.6,
              pb: 0.4,
              mt: 1, // Increased margin from totals
              borderRadius: 1,
              border: '1px solid #dee2e6',
              width: '100%',
              overflow: 'hidden'
            }}>
              <FunctionKey onClick={() => handlePayment('CASH')} sx={{ flex: 1, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#e3f2fd', color: '#1565c0', borderColor: '#bbdefb' }}>
                <span className="key" style={{ color: '#0d47a1', fontSize: '11px' }}>[Space]</span> Cash
              </FunctionKey>

              <FunctionKey onClick={() => (cart.some(it => it.product) ? setSplitBillDialogOpen(true) : enqueueSnackbar('Add items first', { variant: 'warning' }))} sx={{ flex: 1, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#fff3e0', color: '#e65100', borderColor: '#ffe0b2' }}>
                <span className="key" style={{ color: '#ef6c00', fontSize: '11px' }}>[F2]</span> Split
              </FunctionKey>

              <FunctionKey onClick={() => (cart.some(it => it.product) ? handlePayment('CREDIT_CARD') : enqueueSnackbar('Add items first', { variant: 'warning' }))} sx={{ flex: 1, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#f3e5f5', color: '#7b1fa2', borderColor: '#e1bee7' }}>
                <span className="key" style={{ color: '#4a148c', fontSize: '11px' }}>[F3]</span> Card
              </FunctionKey>

              <FunctionKey onClick={() => (cart.some(it => it.product) ? handlePayment('PAYNOW') : enqueueSnackbar('Add items first', { variant: 'warning' }))} sx={{ flex: 1, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#fce4ec', color: '#c2185b', borderColor: '#f8bbd0' }}>
                <span className="key" style={{ color: '#880e4f', fontSize: '11px' }}>[F4]</span> UPI
              </FunctionKey>

              <FunctionKey onClick={() => (cart.some(it => it.product) ? handlePayment('CREDIT') : enqueueSnackbar('Add items first', { variant: 'warning' }))} sx={{ flex: 1, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#fbe9e7', color: '#d84315', borderColor: '#ffccbc' }}>
                <span className="key" style={{ color: '#bf360c', fontSize: '11px' }}>[F5]</span> Credit
              </FunctionKey>

              <FunctionKey onClick={() => setSalesHistoryDialogOpen(true)} sx={{ flex: 1.2, minWidth: 0, px: 0.5, height: '34px', fontSize: '11px', backgroundColor: '#e0f2f1', color: '#00695c', borderColor: '#b2dfdb' }}>
                <span className="key" style={{ color: '#004d40', fontSize: '11px' }}>[Ctrl+V]</span> View
              </FunctionKey>

              <Button
                variant="contained"
                onClick={() => {
                  const validItems = cart.filter(item => item.product && lineItemQty(item) > 0);
                  validItems.length > 0 ? setPaymentDialogOpen(true) : enqueueSnackbar('Add items first', { variant: 'warning' });
                }}
                sx={{
                  flex: 1.5, // Make bill button slightly wider
                  py: 0.5,
                  px: 1,
                  backgroundColor: '#2e7d32', // Darker green
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  textTransform: 'none',
                  minHeight: '34px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  boxShadow: 2,
                  '&:hover': { backgroundColor: '#1b5e20' },
                  ml: 0.4
                }}
                startIcon={<PrintIcon sx={{ fontSize: '17px !important' }} />}
              >
                BILL / PRINT
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialogs logic remains the same, just keeping them in flow */}
      {/* Search Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)}>
        <DialogTitle>Search Customer</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name || ''}
            onChange={(event, newValue) => {
              setCustomer(newValue);
              setCustomerDialogOpen(false);
            }}
            renderInput={(params) => <TextField {...params} label="Customer" autoFocus />}
          />
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialogOpen} onClose={() => setAddCustomerDialogOpen(false)}>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <CustomerForm
            onSuccess={handleCustomerFormSuccess}
            onCancel={() => setAddCustomerDialogOpen(false)}
            initialName={newCustomerName}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h4" align="center" color="primary" gutterBottom>
              {formatCurrency(totals.total)}
            </Typography>
            {/* Customer name and current loyalty points */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body1">
                <strong>Customer:</strong> {customer?.name || 'Walk-in Customer'}
              </Typography>
              {customer?.id && customer?.name && !customer?.name?.toLowerCase?.().includes('walk-in') && (
                <Typography variant="body1" color="primary">
                  <strong>Points:</strong> {customer?.loyaltyPoints ?? 0} pts
                </Typography>
              )}
            </Box>
            {customer?.id && loyaltySettings?.pointValueRupees != null && Number(loyaltySettings.pointValueRupees) > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    const pts = customer?.loyaltyPoints ?? 0;
                    const pointVal = Number(loyaltySettings.pointValueRupees) || 1;
                    const totalVal = Number(totals.total) || 0;
                    const maxRedeem = Math.min(pts, Math.floor(totalVal / pointVal));
                    setRedeemedPoints(maxRedeem);
                  }}
                  disabled={!(customer?.loyaltyPoints > 0 && totals.total > 0)}
                >
                  Redeem
                </Button>
                {redeemedPoints > 0 && (
                  <Button variant="text" size="small" onClick={() => setRedeemedPoints(0)}>Clear</Button>
                )}
                {redeemedPoints > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Redeemed: {redeemedPoints} pts = {formatCurrency(redeemedPoints * (Number(loyaltySettings.pointValueRupees) || 1))} · Amount to pay: {formatCurrency(Math.max(0, totals.total - redeemedPoints * (Number(loyaltySettings.pointValueRupees) || 1)))}
                  </Typography>
                )}
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select value={paymentMethod} label="Payment Method" onChange={(e) => setPaymentMethod(e.target.value)}>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="CREDIT_CARD">Credit Card</MenuItem>
                <MenuItem value="PAYNOW">UPI</MenuItem>
                <MenuItem value="CREDIT">Credit / Pay Later</MenuItem>
                <MenuItem value="SPLIT_BILL">Split Bill</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Salesman (optional)</InputLabel>
              <Select
                value={selectedSalesmanId || ''}
                label="Salesman (optional)"
                onChange={(e) => setSelectedSalesmanId(e.target.value || '')}
              >
                <MenuItem value="">None</MenuItem>
                {salesmen.map((sm) => (
                  <MenuItem key={sm.id} value={sm.id}>{sm.name} {sm.phone ? `(${sm.phone})` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Received Amount"
              type="number"
              value={receivedAmount}
              onChange={(e) => setReceivedAmount(e.target.value)}
              onFocus={(e) => e.target.select()}
              fullWidth
              sx={{
                '& input': {
                  '-moz-appearance': 'textfield',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0,
                  }
                }
              }}
            />
            {paymentMethod === 'CASH' && (
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
                {(() => {
                  const amountToPay = Math.max(0, (totals?.total ?? 0) - (redeemedPoints > 0 ? redeemedPoints * (Number(loyaltySettings?.pointValueRupees) || 1) : 0));
                  const received = parseFloat(receivedAmount) || 0;
                  const balance = amountToPay - received;
                  if (received > 0) {
                    if (balance > 0) {
                      return (
                        <Typography variant="body1" fontWeight="bold" color="error">
                          Balance to Pay: {formatCurrency(balance)}
                        </Typography>
                      );
                    }
                    if (balance < 0) {
                      return (
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          Change to Give: {formatCurrency(Math.abs(balance))}
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        Exact amount received
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Enter received amount to see balance / change
                    </Typography>
                  );
                })()}
              </Box>
            )}
            <TextField label="Notes" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (paymentMethod === 'SPLIT_BILL') {
                setPaymentDialogOpen(false);
                setSplitBillDialogOpen(true);
              } else {
                handlePayment();
              }
            }}
            color="primary"
          >
            Complete Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Bill Dialog */}
      <SplitBillDialog
        open={splitBillDialogOpen}
        onClose={() => setSplitBillDialogOpen(false)}
        totalAmount={Math.max(
          0,
          (totals?.total ?? 0) -
          (redeemedPoints > 0 ? redeemedPoints * (Number(loyaltySettings?.pointValueRupees) || 1) : 0)
        )}
        onConfirm={(splitData) => {
          setSplitBillDialogOpen(false);
          handlePayment(JSON.stringify(splitData));
        }}
        requireCustomerForCredit={true}
        hasCustomer={!!(customer?.id && customer?.name && !customer?.name?.toLowerCase?.().includes('walk-in'))}
      />

      {/* WhatsApp Dialog */}
      <Dialog open={whatsappDialogOpen} onClose={handleWhatsappShareCancel}>
        <DialogTitle>Share Bill on WhatsApp?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to send this bill to the customer via WhatsApp?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWhatsappShareCancel}>No</Button>
          <Button onClick={handleWhatsappShareConfirm} variant="contained" color="success">Yes, Share</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Print Dialog */}
      <Dialog
        open={previewPrintDialogOpen}
        onClose={handlePreviewDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Bill Preview & Print</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handlePreviewDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading bill preview...</Typography>
            </Box>
          ) : previewPdfUrl ? (
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
              <iframe
                src={previewPdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block'
                }}
                title="Bill Preview"
                onError={(e) => {
                  console.error('Error loading PDF in iframe:', e);
                  enqueueSnackbar('Failed to display PDF preview', { variant: 'error' });
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
              <Typography variant="body2" color="text.secondary">Failed to load preview</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handlePreviewDialogClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (previewPdfUrl) {
                window.open(previewPdfUrl, '_blank');
              }
            }}
            variant="outlined"
            disabled={!previewPdfUrl || loadingPreview}
          >
            Open in New Tab
          </Button>
          <Button
            onClick={handlePreviewPrint}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!previewPdfUrl || loadingPreview}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Choice Dialog (Preview Print + WhatsApp) */}
      <Dialog open={printChoiceDialogOpen} onClose={() => { setPrintChoiceDialogOpen(false); clearAll(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Action</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {lastCreatedOrder && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order #{lastCreatedOrder.orderNumber || lastCreatedOrder.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer: {customer?.name || 'Walk-in Customer'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: {formatCurrency(lastCreatedOrder.total || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2, fontWeight: 'medium' }}>
                    What would you like to do?
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPrintChoiceDialogOpen(false);
              // Open preview dialog instead of directly printing
              setPreviewPrintDialogOpen(true);
            }}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Preview & Print
          </Button>
          <Button
            onClick={async () => {
              setPrintChoiceDialogOpen(false);
              // Directly share to WhatsApp without confirmation dialog
              if (!lastCreatedOrder?.id) {
                clearAll();
                return;
              }
              try {
                await shareBillToWhatsApp(
                  lastCreatedOrder.id,
                  customer,
                  lastCreatedOrder.orderNumber || lastCreatedOrder.id
                );
                clearAll();
              } catch (error) {
                console.error('Error sharing to WhatsApp:', error);
                enqueueSnackbar('Failed to share bill to WhatsApp', { variant: 'error' });
                clearAll();
              }
            }}
            variant="contained"
            color="success"
            startIcon={<span>📱</span>}
          >
            WhatsApp Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Scheme Selection Dialog */}
      <Dialog
        open={printSchemeDialogOpen}
        onClose={() => { setPrintSchemeDialogOpen(false); clearAll(); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>Choose Print Scheme</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Print Scheme</InputLabel>
            <Select
              value={selectedPrintScheme}
              label="Print Scheme"
              onChange={(e) => setSelectedPrintScheme(e.target.value)}
            >
              {PRINT_FORMATS.map((format) => (
                <MenuItem key={format.value} value={format.value}>
                  {format.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => { setPrintSchemeDialogOpen(false); clearAll(); }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSchemePrint(lastCreatedOrder, selectedPrintScheme)}
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Earlier-expiry batch reminder when cashier picks a later-expiring batch */}
      <Dialog
        open={Boolean(batchEarlierExpiryDialog)}
        onClose={() => setBatchEarlierExpiryDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Earlier batch expiry</DialogTitle>
        <DialogContent>
          {batchEarlierExpiryDialog && (
            <Typography variant="body2" sx={{ pt: 1 }}>
              Batch <strong>{batchEarlierExpiryDialog.chosenBatch?.batchNumber}</strong> expires on{' '}
              <strong>{formatExpiryForPos(batchEarlierExpiryDialog.chosenBatch?.expiryDate) || '—'}</strong>.
              There is stock in batch <strong>{batchEarlierExpiryDialog.soonerBatch?.batchNumber}</strong> that expires
              sooner on <strong>{formatExpiryForPos(batchEarlierExpiryDialog.soonerBatch?.expiryDate) || '—'}</strong>.
              Continue with the batch you selected?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchEarlierExpiryDialog(null)} variant="outlined">
            Go back
          </Button>
          <Button
            onClick={() => {
              const d = batchEarlierExpiryDialog;
              setBatchEarlierExpiryDialog(null);
              if (d) applyBatchChange(d.rowIndex, d.pendingBatchIdValue);
            }}
            variant="contained"
            color="warning"
          >
            Continue with selected batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            if (document.activeElement === deleteYesRef.current) {
              deleteNoRef.current?.focus();
            } else {
              deleteYesRef.current?.focus();
            }
          }
        }}
      >
        <DialogTitle>Delete Row</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this row ({selectedRowIndex !== null ? selectedRowIndex + 1 : ''})?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            ref={deleteNoRef}
            variant="outlined"
          >
            No
          </Button>
          <Button
            onClick={() => {
              if (rowToDelete !== null) {
                deleteRow(rowToDelete);
                setSelectedRowIndex(null);
                setDeleteConfirmOpen(false);
                setRowToDelete(null);
              }
            }}
            ref={deleteYesRef}
            color="error"
            variant="contained"
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Price Dialog - P key on selected row */}
      <Dialog
        open={purchasePriceDialogOpen}
        onClose={() => { setPurchasePriceDialogOpen(false); setPurchasePriceDialogData(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Purchase Price</DialogTitle>
        <DialogContent>
          {purchasePriceDialogData && (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {purchasePriceDialogData.productName}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {formatCurrency(purchasePriceDialogData.purchasePrice)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPurchasePriceDialogOpen(false); setPurchasePriceDialogData(null); }} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hold Bill Modal */}
      <HoldBillModal
        open={holdBillModalOpen}
        onClose={() => setHoldBillModalOpen(false)}
        customer={customer}
        cartItems={cart.filter(item => item.product)} // Only pass items with actual products
        onSuccess={handleHoldBillSuccess}
      />

      {/* View Hold Bills Modal */}
      <ViewHoldBillsModal
        open={viewHoldBillsModalOpen}
        onClose={() => setViewHoldBillsModalOpen(false)}
        onProcessBill={handleProcessHoldBill}
        headerBg={tableHeaderBg}
      />
      {/* Sales History Dialog (Ctrl+V) */}
      <Dialog
        open={salesHistoryDialogOpen}
        onClose={() => setSalesHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ bgcolor: tableHeaderBg, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center">
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">Sales History</Typography>
          </Box>
          <IconButton onClick={() => setSalesHistoryDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box p={2} sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by Bill No, Customer Name or Amount..."
              value={salesHistorySearch}
              onChange={(e) => setSalesHistorySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { bgcolor: 'white' }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: '60vh' }}>
            {salesHistoryLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={10}>
                <CircularProgress />
              </Box>
            ) : (
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Bill No</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Date</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Customer</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Payment</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Total (₹)</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg }}>Actions</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSalesHistory.length > 0 ? (
                    filteredSalesHistory
                      .slice(salesHistoryPage * salesHistoryRowsPerPage, (salesHistoryPage + 1) * salesHistoryRowsPerPage)
                      .map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell align="center" sx={{ fontWeight: '500' }}>
                            {order.orderNumber || order.id}
                          </TableCell>
                          <TableCell align="center">
                            {formatDate(order.createdAt)}
                          </TableCell>
                          <TableCell align="center">
                            {order.customerName || order.customer?.name || 'Walk-in'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={order.paymentMethod || 'CASH'}
                              size="small"
                              color={order.paymentStatus === 'PAID' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            ₹{(order.total || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" gap={1}>
                              <Tooltip title="Reprint Bill">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleReprintBill(order.id)}
                                  sx={{ border: '1px solid', borderColor: 'rgba(25, 118, 210, 0.2)' }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Bill">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleEditBillFromHistory(order)}
                                  sx={{ border: '1px solid', borderColor: 'rgba(237, 108, 2, 0.2)' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                        <Typography color="text.secondary">No sales history found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredSalesHistory.length}
            rowsPerPage={salesHistoryRowsPerPage}
            page={salesHistoryPage}
            onPageChange={(e, newPage) => setSalesHistoryPage(newPage)}
            onRowsPerPageChange={(e) => {
              setSalesHistoryRowsPerPage(parseInt(e.target.value, 10));
              setSalesHistoryPage(0);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setSalesHistoryDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POSKeyboard;
